import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import axios from "axios";
import JsBarcode from "jsbarcode";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { cardStyle, inputStyle } from "../rule-builder/ui";
import UploadIcon from "@mui/icons-material/Upload";
import UploadFileModal from "../appointments/UploadFileModal";
import LabReportModal from "./LabReportModal";
import DispatchToVendorModal from "./DispatchToVendorModal";
import AssignDiagnosticDoctorsModal from "../diagnostic-reports/AssignDiagnosticDoctorsModal";
import { sendLabSampleToLis } from "../billing-payment/utils/lisIntegration";
import {
  LAB_SERVICE_GROUP_ID,
  fetchDiagnosticReview,
  isReviewSubmitted,
  markReviewSubmitted,
  markReviewRevised,
} from "../diagnostic-reports/diagnosticReportApi";
import {
  OUTSOURCED_LAB_OPTIONS,
  OUTSOURCED_PHLEBOTOMY_OPTIONS,
  clearDispatch,
  isOutsourceable,
  dispatchedStatusLabel,
  outsourcedLabel,
  readDispatchMap,
  saveDispatch,
  wireStatusFor,
} from "./api/outsourcing";
import {
  captureVendorChargesForOrders,
  resolveBillingScope,
  reverseVendorCharges,
} from "./api/vendorChargeCapture";
import {
  downloadLabReport,
  withDoctorPrefix,
} from "./utils/printLabReport";
import {
  READY_STATUSES,
  REPORT_PUBLISHED_STATUSES,
  computeAge,
  formatDateTime,
  STATUS_META,
  PriorityPill,
  groupOrdersByBatch,
  groupOrdersByBatchOnly,
  reconcileOrderBatchKeys,
  isReportGenerated,
  isDiagnosticReportRejected,
  isDiagnosticReportPublished,
  mergeDiagnosticReviewFields,
  flattenObservationPanels,
  normalizeLabResultRows,
  readLabReport,
  rememberLabReport,
  saveLabReportToServer,
  readLabObservationPanels,
  fetchLabTestsPage,
  fetchAllLabTestPatients,
  patientMatchesLabSearch,
  computeLabDashboardStats,
  normalizeLabOrderStatus,
  normalizePriorityLabel,
  resolveOrderPatientLabTestId,
  lookupPatientLabTestId,
  latestActivityMs,
  sortOrdersNewestFirst,
} from "./labOrdersShared";

// Order of the "|"-joined fields a legacy QR-code label could carry. Retained
// only so scanning an older printed label (from before labels switched to a
// 1D barcode) still decodes; current labels encode just the short
// human-readable code (e.g. BC-LAB-2026-0011) below.
const BARCODE_FIELDS = ["barcode", "date", "name", "uhid", "sample", "tests"];

// Decodes a scanned legacy QR payload back into its named fields. Returns
// null for a plain code (what current labels encode, or what someone types
// in by hand) that has no "|".
const parseBarcodePayload = (value) => {
  const parts = value.split("|");
  if (parts.length < BARCODE_FIELDS.length) return null;
  return BARCODE_FIELDS.reduce((acc, key, i) => {
    acc[key] = (parts[i] || "").trim();
    return acc;
  }, {});
};

// Renders the short human-readable code as a 1D CODE128 barcode. Encoding
// just the code (not the full date/name/UHID/sample/tests payload the old QR
// carried) keeps the bars wide enough to scan reliably at the 40mm label
// width. JsBarcode needs a real canvas/SVG node to draw
// into, so we render to a detached canvas and read it back as a data URL —
// keeping this synchronous like the old QR renderer, so the popup write below
// stays synchronous too (avoids pop-up blockers).
const renderBarcodeImgMarkup = (value) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format: "CODE128",
    displayValue: false,
    margin: 4,
    width: 2,
    height: 40,
  });
  return `<img src="${canvas.toDataURL("image/png")}" alt="barcode" />`;
};

// `group` is a { sample, barcode, orders, primaryOrderNo } cluster from
// groupOrdersByBatch — one printed label per sample-type row.
const printBarcodeLabel = (group, patient) => {
  const facilityName =
    sessionStorage.getItem("facility_name") || "Your Facility";
  let barcodeMarkup = "";
  try {
    barcodeMarkup = renderBarcodeImgMarkup(group.barcode);
  } catch (err) {
    console.error("Failed to render barcode for print:", err);
    toast.error("Could not generate barcode label");
    return;
  }

  // The popup only needs to be this big so the browser's own print dialog has
  // room to render its controls — the printed label itself stays a small
  // 40mm x 25mm tube label regardless of how large this window is.
  const win = window.open("", "_blank", "width=460,height=620");
  if (!win) {
    toast.error("Please allow pop-ups to print the barcode label");
    return;
  }

  const servicesMarkup = group.orders
    .map((o) => `<div>${o.service}</div>`)
    .join("");

  win.document.write(`
    <html>
      <head>
        <title>Barcode ${group.barcode}</title>
        <style>
          /* Small tube label — sized to stick onto a specimen tube. */
          @page { size: 40mm 25mm; margin: 0; }
          html, body { margin: 0; padding: 0; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            display: flex;
            justify-content: center;
            padding-top: 12px;
          }
          /* On the actual label print the page IS the label, so drop the
             on-screen centering/offset to avoid pushing content off-page. */
          @media print { body { padding-top: 0; display: block; } }
          .label {
            width: 40mm;
            height: 25mm;
            box-sizing: border-box;
            padding: 1mm 1.5mm;
            border: 0.2mm solid #333;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            gap: 0.5mm;
          }
          /* Top: human-readable info. Bottom: a full-width strip for the
             1D barcode, which needs width (not a square) to stay scannable. */
          .info { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
          .info h2 {
            margin: 0 0 0.5mm;
            font-size: 6.5px;
            font-weight: bold;
            line-height: 1.1;
            border-bottom: 0.2mm solid #333;
            padding-bottom: 0.5mm;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .row { display: flex; justify-content: space-between; gap: 1mm; font-size: 6px; line-height: 1.2; }
          .row .k { color: #555; }
          .service { font-weight: bold; font-size: 6px; line-height: 1.1; margin: 0.3mm 0; }
          .barcode-text { font-family: "Courier New", monospace; font-size: 5.5px; letter-spacing: 0.2px; margin-top: auto; text-align: center; }
          .barcode { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; }
          .barcode img { width: 100%; height: 7mm; object-fit: contain; display: block; }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="info">
            <h2>${facilityName}</h2>
            <div class="row"><span class="k">UHID</span><span>${patient.uhid}</span></div>
            <div class="row"><span class="k">Sample</span><span>${group.sample || "-"}</span></div>
            <div class="service">${servicesMarkup}</div>
            <div class="barcode-text">${group.barcode}</div>
          </div>
          <div class="barcode">${barcodeMarkup}</div>
        </div>
        <script>window.onload = function () { window.print(); };</script>
      </body>
    </html>
  `);
  win.document.close();
};

// ─── Live data: GET get_patient_lab_tests/?page=&page_size=&… ─────────────────
// Paginated All/OPD/IPD board. Filters are server-side query params.
// Detail on expand: same endpoint with admission_id or lab_test_id.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Billing charge-capture wants the numeric service master number (CPT column),
// never a UUID medical_service_id from older lab orders.
const billingMedicalServiceId = (order) => {
  const raw = order?.medicalServiceNo;
  if (raw == null || raw === "" || raw === "-") return null;
  const s = String(raw).trim();
  if (UUID_RE.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && String(n) === s ? n : s;
};

const normalizeServiceKey = (service) =>
  String(service || "")
    .trim()
    .toLowerCase();

const orderSourceRank = (order) => {
  let rank = 0;
  if (/^LAB-/i.test(order.orderNo)) rank += 100;
  if (order.sample && order.sample !== "-") rank += 30;
  if (order.ordered) rank += 20;
  if (order.cpt && order.cpt !== "-") rank += 10;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(order.orderNo)) rank += 5;
  return rank;
};

const dedupePatientOrders = (orders) => {
  const best = new Map();

  (orders || []).forEach((order) => {
    const serviceKey = normalizeServiceKey(order.service);
    if (!serviceKey || serviceKey === "-") return;
    const dedupeKey = order.batchId
      ? `${order.batchId}|${serviceKey}`
      : `${order.orderNo}|${serviceKey}`;
    const existing = best.get(dedupeKey);
    const normalized = { ...order };
    if (!existing || orderSourceRank(normalized) > orderSourceRank(existing)) {
      best.set(dedupeKey, normalized);
    }
  });

  return sortOrdersNewestFirst(reconcileOrderBatchKeys(Array.from(best.values())));
};

// A patient field the row doesn't really have yet: the bulk list defaults
// gender/dob/phone to "-" and age to null, and the report header has to tell
// those apart from a real value before deciding whether to overwrite.
const isMissingValue = (value) =>
  value === undefined || value === null || value === "" || value === "-";

// Demographics from a detail refetch — only keys the response actually carries.
const pickPatientDemographics = (data) => {
  const picked = {};
  const take = (key, value) => {
    if (!isMissingValue(value)) picked[key] = value;
  };
  take("age", data?.age ?? data?.age_years);
  take("gender", data?.gender);
  take("dob", data?.dob ?? data?.date_of_birth);
  take("uhid", data?.uhid);
  take("bedNo", data?.bed_no);
  take("roomNo", data?.room_no);
  take("abhaId", data?.abha_id);
  take("doctorName", data?.doctor_name);
  take("phone", data?.patient_phone_no || data?.phone);
  return picked;
};

// Only the demographics that would actually fill a gap on the row — returning
// {} when there's nothing to add keeps the reference-preserving merge below
// from re-rendering an unchanged row.
const demographicsPatch = (patient, demographics) => {
  const patch = {};
  Object.entries(demographics).forEach(([key, value]) => {
    if (isMissingValue(patient?.[key])) patch[key] = value;
  });
  return patch;
};

const STATUS_OPTIONS = [
  "Ordered",
  "Barcode Generated",
  "Sample Collected",
  "Sample Acknowledged",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Verified",
  "Delivered",
  "Uploaded",
  "Cancelled",
];

// [NO_PHLEBOTOMIST] Facilities with has_phlebotomist = false (get_logged_in_user_info/):
// the Lab User alone runs OPD lab orders through these four statuses, and the
// backend rejects anyone else. IPD and Emergency keep the usual two-role flow.
const LAB_ONLY_STATUSES = [
  "Ordered",
  "Completed",
  "Delivered",
  "Uploaded",
  "Cancelled",
];
const LAB_ONLY_EXTRA_FILTERS = ["Delivered", "Uploaded"];

// What the status filter offers, in the order the run actually happens: a
// sample is acknowledged first, and only then does its label get printed.
// The acknowledgement step reads as plain "Acknowledged" here, the same as it
// does in the row's own column — but the value posted to
// get_patient_lab_tests/ stays the backend's own wording.
const STATUS_FILTER_OPTIONS = [
  "Ordered",
  "Sample Acknowledged",
  "Barcode Generated",
  "Sample Collected",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Submitted for Review",
  "Verified",
  "Cancelled",
];

// "Submitted for Review" is a Reported order already sent to a diagnostic
// doctor — not a backend status, so it is matched here, and "Reported" then
// means finalized but not yet submitted.
const matchesStatusFilter = (order, statusFilter) => {
  const status = normalizeLabOrderStatus(order.status);
  const submitted = status === "Reported" && isReviewSubmitted(order);
  if (statusFilter === "Submitted for Review") return submitted;
  if (statusFilter === "Reported") return status === "Reported" && !submitted;
  return status === statusFilter;
};

const STATUS_FILTER_LABELS = { "Sample Acknowledged": "Acknowledged" };

// A refetch (Refresh button, or collapse/re-expand) right after Acknowledge
// can beat the backend's own write — get_patient_lab_tests/ then still
// reports the pre-update status for a moment, which would otherwise
// stomp the status we just moved forward. Never let a refetch regress an
// order backward along STATUS_OPTIONS; keep whichever side is furthest along.
const statusRank = (status) => {
  const idx = STATUS_OPTIONS.indexOf(normalizeLabOrderStatus(status));
  return idx === -1 ? 0 : idx;
};

const furthestStatus = (...statuses) => {
  const normalized = statuses
    .filter(Boolean)
    .map((s) => normalizeLabOrderStatus(s));
  if (normalized.length === 0) return "Ordered";
  return normalized.reduce(
    (best, s) => (statusRank(s) > statusRank(best) ? s : best),
    normalized[0],
  );
};

// Which step of the lab run bills the patient is a per-facility setting, not a
// fixed point in the flow: get_logged_in_user_info/ returns it as
// `lab_chargecapture_step` ("Sample Acknowledged", "Sample Collected", …).
const DEFAULT_CHARGE_CAPTURE_STEP = "Sample Acknowledged";

// Only the steps this board actually moves a sample to can bill it. "Ordered"
// is where a sample starts, and "Verified"/"Cancelled" are written elsewhere,
// so naming one of those would mean the charge never goes out at all —
// they are deliberately not accepted here.
const CHARGE_CAPTURE_STEPS = [
  DEFAULT_CHARGE_CAPTURE_STEP,
  "Barcode Generated",
  "Sample Collected",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
];

// The setting is written against what the screen calls each step, which is not
// always the backend's own wording: the acknowledgement step reads as plain
// "Acknowledged" in the filter and in the row's column (STATUS_FILTER_LABELS /
// ACK_COLUMN_LABELS) while the status that actually persists is
// "Sample Acknowledged". Configuring it either way means the same step —
// the screen wording is mapped back to the status before it is matched.
const CHARGE_CAPTURE_STEP_ALIASES = {
  acknowledged: DEFAULT_CHARGE_CAPTURE_STEP,
  "sent to lab": "Sample Sent",
};

// Anything the board cannot bill at — a missing flag on a backend that predates
// it, a typo, a wording it doesn't use, or a step it never reaches — falls back
// to capturing at Acknowledge, which is where charges went before the setting
// existed. Charges are never silently skipped because of a bad value.
const resolveChargeCaptureStep = (userInfo) => {
  const raw = String(userInfo?.lab_chargecapture_step || "").trim();
  if (!raw) return DEFAULT_CHARGE_CAPTURE_STEP;
  const wanted = CHARGE_CAPTURE_STEP_ALIASES[raw.toLowerCase()] || raw;
  const match = CHARGE_CAPTURE_STEPS.find(
    (s) => s.toLowerCase() === wanted.toLowerCase(),
  );
  if (!match) {
    console.warn(
      `lab_chargecapture_step "${raw}" is not a step this board bills at — capturing at "${DEFAULT_CHARGE_CAPTURE_STEP}" instead`,
    );
  }
  return match || DEFAULT_CHARGE_CAPTURE_STEP;
};

const reconcileOrderStatuses = (prevOrders, nextOrders, persisted) => {
  if (!prevOrders?.length) return nextOrders;
  const prevByNo = new Map(prevOrders.map((o) => [o.orderNo, o]));
  return nextOrders.map((no) => {
    const po = prevByNo.get(no.orderNo);
    const persistedStatus = persisted?.get(String(no.orderNo));
    const keep = furthestStatus(no.status, po?.status, persistedStatus);
    const merged = keep === no.status ? no : { ...no, status: keep };
    const reviewFields = mergeDiagnosticReviewFields(merged, po);
    return { ...merged, ...reviewFields };
  });
};

const isIpdPatientRow = (patient) =>
  patient?.patientType === "IPD" ||
  (patient?.admissionId != null && patient.admissionId !== "");

const isOpdPatientRow = (patient) =>
  !isIpdPatientRow(patient) &&
  String(patient?.patientType || "OPD").toUpperCase() === "OPD";

// patientRows may stamp `${p.id}::${batchKey}` when one admission has several
// order batches. Status writes must still hit the unsuffixed patients entry.
const samePatientRow = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  if (a.id && b.id) {
    const aId = String(a.id);
    const bId = String(b.id);
    if (aId.startsWith(`${bId}::`) || bId.startsWith(`${aId}::`)) return true;
  }
  if (
    isIpdPatientRow(a) &&
    isIpdPatientRow(b) &&
    a.admissionId != null &&
    String(a.admissionId) === String(b.admissionId)
  ) {
    return true;
  }
  if (
    a.patientType === "EMERGENCY" &&
    b.patientType === "EMERGENCY" &&
    a.visitId != null &&
    b.visitId != null &&
    String(a.visitId) === String(b.visitId)
  ) {
    return true;
  }
  const aAppt = a.appointmentId;
  const bAppt = b.appointmentId;
  if (aAppt && bAppt && String(aAppt) === String(bAppt)) {
    if (!a.patientId || !b.patientId) return true;
    return String(a.patientId) === String(b.patientId);
  }
  return false;
};

/** Sample routed to an external vendor — tracked client-side after dispatch. */
const isOutsourcedOrder = (orderNo, dispatchMap) =>
  !!dispatchMap?.[String(orderNo || "").trim()];

const admissionIdForRow = (row) => {
  if (row?.admissionId != null && row.admissionId !== "") {
    return String(row.admissionId);
  }
  const id = String(row?.id || "");
  const split = id.indexOf("::");
  return split > 0 ? id.slice(0, split) : null;
};

/** Stable list key — row position never changes once assigned for the session. */
const stableRowKey = (row) => {
  const id = String(row?.id || "");
  if (id.includes("::")) return id;
  const adm = admissionIdForRow(row);
  if (adm) return adm;
  return `id:${id}`;
};

const orderVisibleOnBoard = (
  order,
  { workspace, isLabBench, dispatchMap, sessionOrderNos },
) => {
  const no = String(order?.orderNo || "");
  if (sessionOrderNos?.has(no)) {
    if (isLabBench && isOutsourcedOrder(order.orderNo, dispatchMap)) return false;
    return true;
  }
  if (!statusInWorkspace(order.status, workspace)) return false;
  if (isLabBench && isOutsourcedOrder(order.orderNo, dispatchMap)) return false;
  return true;
};

/** True when this rendered row should stay open in the accordion. */
const isRowExpanded = (row, expandedSet) => {
  if (!row || !expandedSet?.size) return false;
  if (expandedSet.has(row.id)) return true;
  const adm = admissionIdForRow(row);
  if (!adm) return false;
  if (expandedSet.has(adm)) return true;
  const rowBatch =
    String(row.id).includes("::") ? String(row.id).slice(adm.length + 2) : "";
  for (const pid of expandedSet) {
    const ps = String(pid);
    if (ps === adm) return true;
    if (!ps.startsWith(`${adm}::`)) continue;
    if (!rowBatch || ps.slice(adm.length + 2) === rowBatch) return true;
  }
  return false;
};

// patientRows may restamp row ids after a refetch or batch reconcile
// (e.g. "506" → "506::batch-…"). Remap expanded ids so a status change
// does not read as the accordion snapping shut.
const remapExpandedRowIds = (prevExpanded, rows) => {
  if (!prevExpanded?.size) return prevExpanded;
  const next = new Set();
  const exact = rows.find((row) => prevExpanded.has(row.id));
  const keep = exact || rows.find((row) => isRowExpanded(row, prevExpanded));
  if (keep) next.add(keep.id);
  if (
    next.size === prevExpanded.size &&
    [...next].every((id) => prevExpanded.has(id))
  ) {
    return prevExpanded;
  }
  return next;
};

const assertStatusUpdateOk = (res) => {
  if (res?.data?.is_valid === false || res?.data?.status === "Failed") {
    throw new Error(res.data?.message || "Request rejected by server");
  }
  return res;
};

// Detail fetch (get_patient_lab_tests/?admission_id= or ?lab_test_id=) can lag
// a few seconds behind a freshly-placed Order Management / Care Plan save.
// Prefer the detail list for shared orderNos, but keep any orders we already
// have that the detail endpoint hasn't returned yet — otherwise a Refresh
// silently drops brand-new tests from the UI.
const mergeOrderLists = (prevOrders, nextOrders, persisted) => {
  if (!prevOrders?.length) return nextOrders || [];
  if (!nextOrders?.length) return prevOrders;
  const reconciled = reconcileOrderStatuses(prevOrders, nextOrders, persisted);
  const nextNos = new Set(reconciled.map((o) => o.orderNo));
  const extras = prevOrders.filter((o) => !nextNos.has(o.orderNo));
  if (extras.length === 0) return reconciled;
  return dedupePatientOrders([...reconciled, ...extras]);
};

const DIAGNOSTIC_REVIEW_SYNC_MAX = 24;

/** get_patient_lab_tests/ may omit rejection fields; pathologist queue has them. */
const syncLabDiagnosticRejections = async (patients, commitPatients) => {
  const tasks = [];
  for (const patient of patients) {
    for (const order of patient.orders || []) {
      if (isDiagnosticReportPublished(order)) continue;
      if (!isReportGenerated(order)) continue;
      const pltId = resolveOrderPatientLabTestId(order);
      if (!pltId) continue;
      if (
        isDiagnosticReportRejected(order) &&
        String(order.rejectionReason || "").trim()
      ) {
        continue;
      }
      tasks.push({ patient, orderNo: order.orderNo, pltId });
      if (tasks.length >= DIAGNOSTIC_REVIEW_SYNC_MAX) break;
    }
    if (tasks.length >= DIAGNOSTIC_REVIEW_SYNC_MAX) break;
  }
  if (tasks.length === 0) return;

  let newlySubmitted = false;
  const updates = await Promise.all(
    tasks.map(async ({ patient, orderNo, pltId }) => {
      try {
        const review = await fetchDiagnosticReview(pltId, {
          roleType: "pathologist",
        });
        // Already with a diagnostic doctor (survives a reload, unlike the
        // session's own Submit click).
        if (review?.submitted && !isReviewSubmitted({ orderNo })) {
          markReviewSubmitted(orderNo);
          newlySubmitted = true;
        }
        if (!review?.rejection?.rejected && !review?.rejection?.reason) {
          return null;
        }
        return {
          patient,
          orderNo,
          reportPublishStatus: "rejected",
          rejectionReason:
            review.rejection.reason ||
            review.rejectionReason ||
            "",
        };
      } catch {
        return null;
      }
    }),
  );

  const patches = updates.filter(Boolean);
  if (patches.length === 0) {
    // Nothing to patch, but the board has to repaint to show the new label.
    if (newlySubmitted) commitPatients((prev) => prev.map((p) => ({ ...p })));
    return;
  }

  commitPatients((prev) =>
    prev.map((p) => {
      const rowPatches = patches.filter((u) => samePatientRow(p, u.patient));
      if (rowPatches.length === 0) return p;
      return {
        ...p,
        orders: p.orders.map((o) => {
          const patch = rowPatches.find((u) => u.orderNo === o.orderNo);
          if (!patch) return o;
          return {
            ...o,
            reportPublishStatus: patch.reportPublishStatus,
            rejectionReason: patch.rejectionReason || o.rejectionReason || "",
          };
        }),
      };
    }),
  );
};

const PRIORITY_OPTIONS = ["Routine", "Urgent", "STAT"];

const KPI_META = [
  {
    key: "total",
    label: "Total Orders",
    icon: "invoice",
    bg: "#E9F5FE",
    color: C.primary,
  },
  {
    key: "pending",
    label: "Pending",
    icon: "clock",
    bg: "#FFF7ED",
    color: "#C2410C",
  },
  {
    key: "collectionPending",
    label: "Collection Pending",
    icon: "flask",
    bg: "#FEF3F2",
    color: "#D97706",
  },
  {
    key: "samplesReceived",
    label: "Samples Received",
    icon: "flask",
    bg: "#EFF6FF",
    color: "#1D4ED8",
  },
  {
    key: "processing",
    label: "Processing",
    icon: "sync",
    bg: "#F3E8FF",
    color: "#7C3AED",
  },
  {
    key: "completed",
    label: "Completed",
    icon: "check",
    bg: "#ECFDF5",
    color: "#15803D",
  },
  {
    key: "verified",
    label: "Verified",
    icon: "shield",
    bg: "#F0FDFA",
    color: "#0F766E",
  },
  {
    key: "today",
    label: "Today",
    icon: "clock",
    bg: "#FEF2F2",
    color: "#B91C1C",
  },
];

const EMPTY_KPI_STATS = {
  total: 0,
  pending: 0,
  collectionPending: 0,
  samplesReceived: 0,
  processing: 0,
  completed: 0,
  verified: 0,
  today: 0,
};

function KpiStrip({ stats, loading }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {KPI_META.map((k) => (
        <div
          key={k.key}
          style={{
            ...cardStyle,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: k.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SvgIcon name={k.icon} size={16} color={k.color} />
          </div>
          <div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {k.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {loading ? "…" : stats[k.key]}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const formatPatientLocation = (patient) => {
  const room =
    patient?.roomNo && patient.roomNo !== "-"
      ? String(patient.roomNo).trim()
      : "";
  const bed =
    patient?.bedNo && patient.bedNo !== "-" ? String(patient.bedNo).trim() : "";
  const age =
    patient?.age !== null && patient?.age !== undefined && patient.age !== ""
      ? `${patient.age} Y`
      : "";
  let location = "-";
  if (room && bed) location = `${room} · Bed ${bed}`;
  else if (room) location = room;
  else if (bed) location = `Bed ${bed}`;
  // OPD (no bed/room): show OPD instead of appointment mode / date when available.
  if (location === "-" && (patient?.patientType === "OPD" || patient?.patientType === "")) {
    location = "OPD";
  }
  // An emergency order has no bed or room either, but it is not a walk-in —
  // say where it came from rather than leaving a bare dash.
  if (location === "-" && patient?.patientType === "EMERGENCY") {
    location = "Emergency";
  }
  if (age) return location === "-" ? age : `${age} · ${location}`;
  return location;
};

const formatOrderNoLabel = (orderNo) => {
  const s = String(orderNo || "").trim();
  if (s.length <= 18) return s;
  if (/^[0-9a-f-]{20,}$/i.test(s)) return `${s.slice(0, 8)}…${s.slice(-4)}`;
  return `${s.slice(0, 14)}…`;
};

// ── Role split: phlebotomy vs lab bench ──────────────────────────────────────
// Two roles share this screen. The phlebotomist (phlebotomist1) owns the
// pre-analytical run — Ordered → Acknowledge → Barcode Generated → Sample
// Collected — and their job ends the moment they mark the sample Sent. From
// "Sample Sent" onward the order belongs to the lab user (mednovaLab), who
// receives, processes and reports it. So each role only sees the orders that
// are actually theirs to act on.
//
// [ROLE_NAME] Matched loosely on the role string from get_logged_in_user_info/
// (userInfo.role_permission[0].role) because the exact wording is set by the
// backend. Anything unrecognised falls through to "all", which is the old
// behaviour — a role we can't classify sees everything rather than an
// empty screen.
// "Sample Sent" is in BOTH lists on purpose: it is the handover point, so it
// appears on the lab's queue as new work while staying on the phlebotomist's
// board marked "Sent to lab" — their run is finished, but the patient does not
// vanish out from under them the moment they press send.
// Everything past the handover is listed here too, but read-only: the
// phlebotomist can no longer move the status or generate anything, they just
// keep watching their own run until the lab publishes the report (which is
// what turns their Report column from "Not generated" into "View"). Outsourced
// vendor samples are the exception — once dispatched, the phlebotomist uploads
// the vendor PDF directly because no in-house bench generates that report.
// Dropping those statuses instead would make the row disappear at "Sample
// Received" and the report could never surface on their screen at all.
const PHLEBOTOMY_STATUSES = [
  "Ordered",
  "Sample Acknowledged",
  "Barcode Generated",
  "Sample Collected",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Verified",
];
const LAB_BENCH_STATUSES = [
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Verified",
];
// Terminal and shown to both — a cancelled order should never silently vanish.
const ALWAYS_VISIBLE_STATUSES = ["Cancelled"];

const resolveWorkspace = (roleName) => {
  // roleType from login is the reliable split for who sends vs who receives.
  const roleType = String(
    sessionStorage.getItem("roleType") || "",
  ).trim().toLowerCase();
  if (roleType.includes("phleb")) return "phlebotomy";
  if (roleType === "lab user" || roleType.includes("lab")) return "lab";

  const role = String(roleName || "").trim().toLowerCase();
  if (!role) return "all";
  if (role.includes("phleb")) return "phlebotomy";
  if (role.includes("lab")) return "lab";
  return "all";
};

const statusInWorkspace = (status, workspace) => {
  const normalized = normalizeLabOrderStatus(status);
  if (workspace === "all") return true;
  if (ALWAYS_VISIBLE_STATUSES.includes(normalized)) return true;
  const allowed =
    workspace === "phlebotomy" ? PHLEBOTOMY_STATUSES : LAB_BENCH_STATUSES;
  if (allowed.includes(normalized)) return true;
  // Unknown backend label that still normalizes to a known lifecycle step.
  return allowed.some((s) => s.toLowerCase() === normalized.toLowerCase());
};

// Acknowledgement is a point the batch passes through, not a status it sits on:
// the moment the label is printed it becomes "Barcode Generated", then
// Collected, Sent, and so on. Anything at or past that point counts as
// acknowledged — testing for the literal "Sample Acknowledged" made the row
// fall back to offering Acknowledge again as soon as it moved forward.
// The same list gates the barcode, which only means something once there is a
// sample to put it on.
const ACKNOWLEDGED_OR_LATER_STATUSES = [
  "Sample Acknowledged",
  "Barcode Generated",
  "Sample Collected",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Verified",
];

const isAcknowledgedOrLater = (orders) =>
  (orders || []).length > 0 &&
  orders.every((o) => ACKNOWLEDGED_OR_LATER_STATUSES.includes(o.status));

const isBarcodeVisible = isAcknowledgedOrLater;

// Once acknowledged, the column stops sitting on "Acknowledged" and follows
// the run instead, reporting whatever status the expanded sample rows show.
// Two of those read better here than their raw backend wording.
const ACK_COLUMN_LABELS = {
  "Sample Acknowledged": "Acknowledged",
  "Sample Sent": "Sent to lab",
};

const PHLEBOTOMY_STATUS_DISPLAY = {
  "Sample Collected": "Sample Collected",
  "Sample Sent": "Sent to lab",
};

const PHLEBOTOMY_DISPLAY_TO_WIRE = {
  "Sample Collected": "Sample Collected",
  "Sent to lab": "Sample Sent",
};

const wireStatusFromDisplay = (display, { outsourced, isLabBench } = {}) => {
  if (outsourced) return wireStatusFor(display);
  if (!isLabBench && PHLEBOTOMY_DISPLAY_TO_WIRE[display]) {
    return PHLEBOTOMY_DISPLAY_TO_WIRE[display];
  }
  return display;
};

const displayStatusForBoard = (
  wireStatus,
  { outsourced, dispatch, isLabBench } = {},
) => {
  if (outsourced && dispatch) {
    return dispatchedStatusLabel(wireStatus, dispatch);
  }
  if (outsourced) return outsourcedLabel(wireStatus);
  if (!isLabBench && PHLEBOTOMY_STATUS_DISPLAY[wireStatus]) {
    return PHLEBOTOMY_STATUS_DISPLAY[wireStatus];
  }
  return wireStatus;
};

const isHandoverToLabStatus = (status) => {
  const value = String(status || "").trim().toLowerCase();
  return value === "sample sent" || value === "sent to lab";
};

// What the phlebotomist moves a sample to by hand once the label is on it.
// Everything earlier is driven by Acknowledge / printing the label, and
// everything later belongs to the lab bench, not this screen.
const MANUAL_STATUS_OPTIONS = ["Sample Collected", "Sent to lab"];

// The dropdown only appears once the label physically exists. Up to and
// including "Sample Acknowledged" the status is driven by Acknowledge and by
// opening the barcode, so it reads as plain text with nothing to pick.
const STATUS_EDITABLE_FROM = ["Barcode Generated", ...MANUAL_STATUS_OPTIONS];

// The bench picks up where the phlebotomist left off: a sent sample is
// received, processed and completed. Verified is a separate sign-off, not a
// value moved to from this table.
const LAB_MANUAL_STATUS_OPTIONS = ["Sample Received", "Processing", "Completed"];
const LAB_STATUS_EDITABLE_FROM = ["Sample Sent", ...LAB_MANUAL_STATUS_OPTIONS];

const statusTextStyle = (meta) => ({
  padding: 0,
  border: "none",
  background: "transparent",
  color: meta.color,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  outline: "none",
});

// A row is one sample: the tests in it were drawn into the same tube, carry the
// same barcode and travel through the lab together, so they also share one
// status. Where a refetch (or older per-test edits) leaves them disagreeing,
// the row reports the least-advanced of them — the whole tube is only as far
// along as its slowest test, and moving the row forward moves every test in it.
const resolveGroupStatus = (orders) =>
  (orders || []).reduce(
    (slowest, o) =>
      !slowest || statusRank(o.status) < statusRank(slowest)
        ? o.status
        : slowest,
    null,
  ) || "Ordered";

// Plain status text; once editable, the native dropdown arrow sits beside it.
// Which values are on offer depends on whose screen this is.
// `status` is always the backend's own value. An outsourced row rides the same
// wire statuses (see api/outsourcing.js) but reads differently and offers a
// different set of moves — nobody here receives or processes that sample — so
// the options are built in label space and mapped back before they're posted.
function LabStatusSelect({
  status,
  onChange,
  isLabBench,
  outsourced,
  dispatch,
  submitted = false,
  labOnlyOpd = false,
}) {
  const meta = STATUS_META[status] || { bg: "#F3F4F6", color: "#374151" };
  if (submitted) {
    return <span style={statusTextStyle(meta)}>Submitted for Review</span>;
  }
  if (labOnlyOpd) {
    // Only the next steps of the run are offered (Cancelled is always last);
    // Uploaded and Cancelled both end it, and Cancelled is only on offer while
    // the order is still Ordered or Completed.
    const canCancel = status === "Ordered" || status === "Completed";
    const forward = LAB_ONLY_STATUSES.filter(
      (s) =>
        statusRank(s) > statusRank(status) &&
        (s !== "Cancelled" || canCancel),
    );
    if (
      forward.length === 0 ||
      status === "Cancelled" ||
      status === "Uploaded"
    ) {
      return <span style={statusTextStyle(meta)}>{status}</span>;
    }
    return (
      <select
        value={status}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ ...statusTextStyle(meta), cursor: "pointer" }}
      >
        {[status, ...forward].map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    );
  }
  const editableFrom = isLabBench ? LAB_STATUS_EDITABLE_FROM : STATUS_EDITABLE_FROM;
  const manualOptions = outsourced
    ? isLabBench
      ? OUTSOURCED_LAB_OPTIONS
      : OUTSOURCED_PHLEBOTOMY_OPTIONS
    : isLabBench
      ? LAB_MANUAL_STATUS_OPTIONS
      : MANUAL_STATUS_OPTIONS;
  const shown = displayStatusForBoard(status, {
    outsourced,
    dispatch,
    isLabBench,
  });

  // After dispatch the phlebotomist must not walk a sample back to Collected.
  const lockedAfterDispatch =
    outsourced &&
    dispatch &&
    !isLabBench &&
    statusRank(status) >= statusRank("Sample Sent");

  if (lockedAfterDispatch || !editableFrom.includes(status)) {
    return <span style={statusTextStyle(meta)}>{shown}</span>;
  }
  const options = manualOptions.includes(shown)
    ? manualOptions
    : [shown, ...manualOptions];
  return (
    <select
      value={shown}
      onChange={(e) =>
        onChange(
          wireStatusFromDisplay(e.target.value, { outsourced, isLabBench }),
        )
      }
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ ...statusTextStyle(meta), cursor: "pointer" }}
    >
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function RejectedReportDialog({
  order,
  reason,
  loading,
  onClose,
  onRegenerate,
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.45)",
          zIndex: 1400,
        }}
      />
      <div
        role="dialog"
        aria-labelledby="rejected-report-title"
        style={{
          position: "fixed",
          zIndex: 1401,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          maxWidth: "92%",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
          padding: "20px 22px 16px",
        }}
      >
        <div
          id="rejected-report-title"
          style={{ fontSize: 16, fontWeight: 700, color: C.text }}
        >
          Report rejected
        </div>
        <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>
          {order?.orderNo ? `Order ${order.orderNo}` : "This report"} was rejected
          by the reviewing doctor. Status stays Reported until you regenerate
          and submit it again.
        </div>
        <div
          style={{
            marginTop: 14,
            padding: "12px 14px",
            borderRadius: 8,
            border: "1px solid #f5b7b1",
            background: "#fdedec",
            color: "#922b21",
            fontSize: 13,
            lineHeight: 1.45,
            minHeight: 48,
          }}
        >
          {loading
            ? "Loading rejection reason…"
            : reason || "No rejection message was provided."}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: C.primary,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Regenerate Report
          </button>
        </div>
      </div>
    </>
  );
}

function PatientRowBase({
  patient,
  expanded,
  detailLoading,
  onToggle,
  highlightOrderNos,
  onPrintBarcode,
  onGenerateReport,
  onOpenRejected,
  onUploadReport,
  onDownloadConsolidated,
  onAcknowledge,
  onStatusChange,
  acknowledgedAt,
  acknowledging,
  isLabBench,
  isPhlebotomy,
  dispatchMap,
  labOnlyMode = false,
}) {
  const labOnlyOpd = labOnlyMode && isOpdPatientRow(patient);
  const testsCount = patient.orders.length;
  const readyCount = patient.orders.filter((o) =>
    READY_STATUSES.includes(o.status),
  ).length;
  const pendingCount = testsCount - readyCount;
  // Derived from real order status (not just acknowledgedByRow, which is
  // local-only and resets on refresh) so the green badge below still shows
  // after a refresh once the fetched orders come back "Sample Acknowledged"
  // — the backend's actual recognized status value (see STATUS_OPTIONS);
  // a plain "Acknowledged" isn't part of its status vocabulary and silently
  // fails to persist even though update_lab_test_status/ returns success.
  // Anything further along the run counts too — see
  // ACKNOWLEDGED_OR_LATER_STATUSES.
  const isAcknowledged = isAcknowledgedOrLater(patient.orders);
  // Least-advanced status across the row — the same value each sample row in
  // the expanded panel reports — so the column moves along with them. Before
  // the fetched orders catch up, a just-acknowledged row still reads
  // "Acknowledged" off acknowledgedAt.
  const rowStatus = isAcknowledged
    ? resolveGroupStatus(patient.orders)
    : "Sample Acknowledged";
  // "Sent to lab" would be a lie once the sample is on a courier to a vendor,
  // so a dispatched patient's column follows the outsourced wording instead.
  const rowDispatch =
    patient.orders
      .map((o) => dispatchMap[String(o.orderNo)])
      .find(Boolean) || null;
  const rowStatusLabel = rowDispatch
    ? dispatchedStatusLabel(rowStatus, rowDispatch)
    : rowStatus === "Reported" &&
        patient.orders.some((o) => isReviewSubmitted(o))
      ? "Submitted for Review"
      : ACK_COLUMN_LABELS[rowStatus] || rowStatus;
  const rowStatusColor = STATUS_META[rowStatus]?.color || C.success;
  const latestOrder = patient.orders.reduce(
    (latest, o) => (!latest || o.ordered > latest ? o.ordered : latest),
    null,
  );

  const allReady = testsCount > 0 && readyCount === testsCount;
  const isPartial = testsCount > 1 && readyCount > 0 && readyCount < testsCount;

  const orderBatches = useMemo(
    () => groupOrdersByBatch(patient.orders),
    [patient.orders],
  );

  return (
    <>
      <tr style={{ borderTop: `1px solid ${C.border}` }}>
        <td
          style={{ padding: "10px 12px", width: 28, cursor: "pointer" }}
          onClick={() => onToggle(patient)}
          title={expanded ? "Collapse" : "Expand"}
        >
          <span
            style={{
              display: "inline-flex",
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
            }}
          >
            <SvgIcon name="chevron-down" size={16} color={C.textSub} />
          </span>
        </td>
        <td style={{ padding: "10px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: C.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SvgIcon name="users" size={14} color={C.primary} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {patient.name}
              </div>
              <div style={{ fontSize: 11, color: C.textSub }}>
                {formatPatientLocation(patient)}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {patient.uhid}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {testsCount}
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 600,
            color: C.success,
          }}
        >
          {readyCount}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {pendingCount}
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontSize: 12,
            color: C.textSub,
            whiteSpace: "nowrap",
          }}
        >
          {formatDateTime(latestOrder)}
        </td>
        {/* Acknowledging is the phlebotomist's step — by the time an order
            reaches the lab bench it is long past, so the column isn't part of
            the lab user's table at all (see the header, which drops it too). */}
        {!isLabBench && !labOnlyOpd && (
          <td
            style={{ padding: "10px 12px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {isAcknowledged || acknowledgedAt ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    fontWeight: 700,
                    color: rowStatusColor,
                  }}
                >
                  <SvgIcon name="check" size={13} color={rowStatusColor} />
                  {rowStatusLabel}
                </div>
                {acknowledgedAt && (
                  <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                    {formatDateTime(acknowledgedAt)}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onAcknowledge(patient)}
                disabled={acknowledging}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 7,
                  border: `1.5px solid ${C.border}`,
                  background: "#fff",
                  color: C.text,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: acknowledging ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: acknowledging ? 0.6 : 1,
                }}
              >
                <SvgIcon name="check" size={14} color={C.textSub} />
                {acknowledging ? "Acknowledging…" : `Acknowledge (${testsCount})`}
              </button>
            )}
          </td>
        )}
        <td
          style={{ padding: "10px 12px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() =>
              onDownloadConsolidated(patient, {
                readyCount,
                testsCount,
                allReady,
              })
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: isPartial ? "#B45309" : C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <SvgIcon
              name="download"
              size={14}
              color={isPartial ? "#B45309" : C.textSub}
            />
            {isPartial ? `Partial (${readyCount}/${testsCount})` : "PDF"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td
            colSpan={isLabBench || labOnlyOpd ? 8 : 9}
            style={{ padding: "12px 16px", background: "#eef6f6", maxWidth: 0 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#fff",
                border: "1.5px solid #21cdc0",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(33,205,192,0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div style={{ overflowX: "auto", maxWidth: "100%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                <thead>
                  <tr
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      background: "#0E588E",
                    }}
                  >
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Order No
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Sample Type
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Service
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      CPT
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Priority
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Doctor
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Ordered
                    </th>
                    {!labOnlyOpd && (
                      <th style={{ textAlign: "left", padding: "8px 12px" }}>
                        Barcode
                      </th>
                    )}
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>
                      Status
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 12px", minWidth: 100 }}>
                      Report
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Only show the interstitial loading row when there's nothing
                    cached to display yet — the bulk list already has this
                    patient's orders in almost every case, so the background
                    re-fetch on expand should just swap the rows in silently
                    once it resolves. Showing this row unconditionally made it
                    pop in above the already-rendered orders and then vanish a
                    moment later, which read as the dropdown "blinking" open. */}
                  {detailLoading && patient.orders.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: "10px 12px",
                          fontSize: 12,
                          color: C.textSub,
                          textAlign: "center",
                        }}
                      >
                        Refreshing tests…
                      </td>
                    </tr>
                  )}
                  {orderBatches.map((g) => {
                    const isMulti = g.orders.length > 1;
                    const isHighlighted = g.orders.some((o) =>
                      highlightOrderNos.has(o.orderNo),
                    );
                    // Every test on a sample travels together, so one dispatch
                    // covers the row — the first test carrying one speaks for it.
                    const dispatch =
                      g.orders
                        .map((o) => dispatchMap[String(o.orderNo)])
                        .find(Boolean) || null;
                    return (
                      <tr
                        key={g.groupKey || g.batchKey}
                        style={{
                          borderTop: `1px solid ${C.border}`,
                          background: isHighlighted ? "#fff8d6" : "transparent",
                          transition: "background 0.4s",
                        }}
                      >
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={g.primaryOrderNo}
                        >
                          {formatOrderNoLabel(g.primaryOrderNo)}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            color: C.text,
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{g.sample}</div>
                          {isMulti && (
                            <div
                              style={{
                                fontSize: 11,
                                color: C.textSub,
                                marginTop: 2,
                              }}
                            >
                              {g.orders.length} Tests
                            </div>
                          )}
                          {/* Where the tube physically is. Both roles need it:
                              the phlebotomist to know their run went out, the
                              lab user to know why no sample ever arrived. */}
                          {dispatch && (
                            <div
                              title={
                                dispatch.dispatchRef
                                  ? `Dispatch ref ${dispatch.dispatchRef}`
                                  : undefined
                              }
                              style={{
                                display: "inline-block",
                                marginTop: 4,
                                padding: "2px 6px",
                                borderRadius: 4,
                                background: "#FFF7ED",
                                color: "#C2410C",
                                fontSize: 10,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              → {dispatch.vendorName}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            color: C.text,
                          }}
                        >
                          {g.orders.map((o, idx) => (
                            <div
                              key={o.orderNo}
                              style={{
                                marginTop: idx > 0 ? 4 : 0,
                                lineHeight: 1.35,
                              }}
                            >
                              {o.service}
                            </div>
                          ))}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            color: C.textSub,
                          }}
                        >
                          {g.orders.map((o) => (
                            <div key={o.orderNo}>{o.cpt}</div>
                          ))}
                        </td>
                        <td style={{ padding: "9px 12px" }}>
                          {g.orders.map((o) => (
                            <div
                              key={o.orderNo}
                              style={{ marginBottom: isMulti ? 4 : 0 }}
                            >
                              <PriorityPill priority={o.priority} />
                            </div>
                          ))}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            color: C.text,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.orders[0].doctor && g.orders[0].doctor !== "-"
                            ? g.orders[0].doctor
                            : patient.doctorName}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 11,
                            color: C.textSub,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDateTime(g.orders[0].ordered)}
                        </td>
                        {/* A barcode only exists once the batch has been
                            acknowledged — before that there is no sample to
                            label, so the code is withheld rather than shown
                            greyed out. */}
                        {!labOnlyOpd && (
                        <td style={{ padding: "9px 12px" }}>
                          {isBarcodeVisible(g.orders) ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: C.text,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {g.barcode}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: C.success,
                                    fontWeight: 600,
                                  }}
                                >
                                  Generated
                                </div>
                              </div>
                              <button
                                type="button"
                                title="Print barcode label"
                                onClick={() => onPrintBarcode(g, patient)}
                                style={{
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  padding: 3,
                                  display: "flex",
                                }}
                              >
                                <SvgIcon
                                  name="printer"
                                  size={14}
                                  color={C.textSub}
                                />
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: C.textSub }}>
                              Acknowledge to generate
                            </span>
                          )}
                        </td>
                        )}
                        {/* One sample, one status — every test in this row
                            moves together, exactly like the one barcode they
                            share. */}
                        <td
                          style={{ padding: "9px 12px" }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <LabStatusSelect
                            status={resolveGroupStatus(g.orders)}
                            submitted={
                              resolveGroupStatus(g.orders) === "Reported" &&
                              (g.orders || []).some((o) => isReviewSubmitted(o))
                            }
                            isLabBench={isLabBench}
                            labOnlyOpd={labOnlyOpd}
                            outsourced={!!dispatch}
                            dispatch={dispatch}
                            onChange={(next) =>
                              onStatusChange(g.orders, next, patient, g)
                            }
                          />
                        </td>
                        {/* One report per row too: it covers every test on
                            this sample. In-house runs: the phlebotomist reads
                            "Not generated" until the bench publishes, then
                            "Pending review" until a pathologist publishes.
                            Outsourced vendor runs: once dispatched, the
                            phlebotomist uploads the PDF the vendor sends back. */}
                        <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                          {(() => {
                            const uploadBtnStyle = {
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "5px 10px",
                              borderRadius: 6,
                              border: `1.5px solid ${C.border}`,
                              background: "#fff",
                              color: C.primary,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              whiteSpace: "nowrap",
                            };
                            // OPD + Emergency use the same generate → diagnostic
                            // review path as IPD. Upload stays available on the
                            // lab bench so a verified PDF can still be filed to
                            // health records for phlebotomy / ordering doctor.
                            const isNonIpd = patient.patientType !== "IPD";
                            const showOpdUpload = isNonIpd && isLabBench;
                            const uploadButton = (
                              <button
                                type="button"
                                title="Upload report to health records"
                                onClick={() => onUploadReport(patient, g)}
                                style={uploadBtnStyle}
                              >
                                <UploadIcon sx={{ fontSize: 14 }} />
                                Upload
                              </button>
                            );
                            const withOpdUpload = (node) =>
                              showOpdUpload ? (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    gap: 6,
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {node}
                                  {uploadButton}
                                </span>
                              ) : (
                                node
                              );
                            const groupStatus = resolveGroupStatus(g.orders);
                            const reported = isReportGenerated({
                              status: groupStatus,
                              orderNo: g.primaryOrderNo,
                            });
                            // No phlebotomist at this facility: no report is generated
                            // here, the Lab User only uploads one.
                            if (labOnlyOpd && !reported) return uploadButton;
                            const rejectedOrder = (g.orders || []).find((o) =>
                              isDiagnosticReportRejected(o),
                            );
                            if (rejectedOrder) {
                              return withOpdUpload(
                                <button
                                  type="button"
                                  title="View rejection and regenerate report"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenRejected?.(g, patient, rejectedOrder);
                                  }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "5px 10px",
                                    borderRadius: 6,
                                    border: "1.5px solid #c0392b",
                                    background: "#fdedec",
                                    color: "#c0392b",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Rejected
                                </button>,
                              );
                            }
                            const vendorReportUploadReady =
                              !reported &&
                              dispatch &&
                              ((isPhlebotomy &&
                                statusRank(groupStatus) >=
                                  statusRank("Sample Sent")) ||
                                (isLabBench && groupStatus === "Completed"));
                            if (vendorReportUploadReady) {
                              return (
                                <button
                                  type="button"
                                  title={`Upload the report received from ${dispatch.vendorName}`}
                                  onClick={() => onUploadReport(patient, g)}
                                  style={uploadBtnStyle}
                                >
                                  <UploadIcon sx={{ fontSize: 14 }} />
                                  Vendor report
                                </button>
                              );
                            }
                            if (
                              !reported &&
                              dispatch &&
                              isLabBench &&
                              groupStatus !== "Completed"
                            ) {
                              return withOpdUpload(
                                <span style={{ fontSize: 11, color: C.textSub }}>
                                  Awaiting vendor
                                </span>,
                              );
                            }
                            if (!reported && isPhlebotomy) {
                              return (
                                <span style={{ fontSize: 11, color: C.textSub }}>
                                  Not generated
                                </span>
                              );
                            }
                            if (!reported && isLabBench && groupStatus !== "Completed") {
                              return withOpdUpload(
                                <span style={{ fontSize: 11, color: C.textSub }}>
                                  Complete test first
                                </span>,
                              );
                            }
                            if (!reported) {
                              return withOpdUpload(
                                <button
                                  type="button"
                                  title={`Generate report (${g.orders.length} test${g.orders.length === 1 ? "" : "s"})`}
                                  onClick={() => onGenerateReport(g, patient)}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "5px 10px",
                                    borderRadius: 6,
                                    border: `1.5px solid ${C.border}`,
                                    background: "#fff",
                                    color: C.primary,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <SvgIcon
                                    name="reports"
                                    size={13}
                                    color={C.primary}
                                  />
                                  Generate
                                </button>,
                              );
                            }
                            const publishedForPhlebotomy = (g.orders || []).some(
                              (o) => isDiagnosticReportPublished(o),
                            );
                            if (
                              isPhlebotomy &&
                              reported &&
                              !publishedForPhlebotomy
                            ) {
                              return (
                                <span style={{ fontSize: 11, color: "#B45309" }}>
                                  Pending review
                                </span>
                              );
                            }
                            return withOpdUpload(
                              <button
                                type="button"
                                title={`View report (${g.orders.length} test${g.orders.length === 1 ? "" : "s"})`}
                                onClick={() => onGenerateReport(g, patient)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  padding: "5px 10px",
                                  borderRadius: 6,
                                  border: `1.5px solid ${C.border}`,
                                  background: "#fff",
                                  color: C.primary,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <SvgIcon
                                  name="reports"
                                  size={13}
                                  color={C.primary}
                                />
                                View
                              </button>,
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Memoized so the 30s background poll (or any unrelated parent re-render) only
// repaints a row whose own props actually changed. Combined with the stable
// callbacks and reference-preserving merge, an unchanged list produces zero
// row re-renders — killing the periodic flicker.
const PatientRow = React.memo(PatientRowBase);

const DEFAULT_PAGE_SIZE = 25;

/** Replace page results while preserving optimistic writes and session-visible rows. */
const replacePatientsPreservingStatus = (
  prev,
  next,
  persisted,
  sessionOrderNos,
  { retainSessionExtras = true } = {},
) => {
  const prevById = new Map(prev.map((p) => [String(p.id), p]));
  const prevByAdmission = new Map();
  prev.forEach((p) => {
    if (p.admissionId != null && p.admissionId !== "") {
      prevByAdmission.set(String(p.admissionId), p);
    }
  });

  const coveredAdmissionIds = new Set();
  const merged = next.map((np) => {
    if (np.admissionId != null && np.admissionId !== "") {
      coveredAdmissionIds.add(String(np.admissionId));
    }
    const pp =
      prevById.get(String(np.id)) ||
      (np.admissionId != null
        ? prevByAdmission.get(String(np.admissionId))
        : null);
    if (!pp) return np;
    return {
      ...np,
      orders: mergeOrderLists(pp.orders, np.orders, persisted),
    };
  });

  const coveredIds = new Set(next.map((p) => String(p.id)));
  const orderKept = (o) => {
    const no = String(o.orderNo);
    return persisted?.has(no) || sessionOrderNos?.has(no);
  };
  if (!retainSessionExtras) return merged;

  const extras = prev.filter((p) => {
    if (coveredIds.has(String(p.id))) return false;
    if (
      p.admissionId != null &&
      p.admissionId !== "" &&
      coveredAdmissionIds.has(String(p.admissionId))
    ) {
      return false;
    }
    return (p.orders || []).some(orderKept);
  });

  return extras.length ? [...merged, ...extras] : merged;
};

const hasActiveLabListFilters = ({
  search,
  dateFrom,
  dateTo,
  priority,
  status,
  patientType,
} = {}) =>
  Boolean(String(search || "").trim()) ||
  Boolean(dateFrom) ||
  Boolean(dateTo) ||
  (priority && priority !== "All priorities") ||
  (status && status !== "All statuses") ||
  // A non-ALL encounter tab must drop the previous board — otherwise session
  // stickiness (every visible order is recorded below) keeps IPD rows on the
  // OPD/Emergency view and the tabs look broken.
  (Boolean(patientType) && String(patientType).toUpperCase() !== "ALL");

export default function IpdLabOrders({
  patientTypeFilter = "ALL",
} = {}) {
  const { userInfo, permissions } = usePermissions();
  const canAssignDiagnosticDoctor = permissions.some(
    (p) =>
      p.permission_name === "Assign Diagnostic Doctor" && p.is_accessible,
  );
  const brandingData = useContext(BrandingContext);
  const workspace = resolveWorkspace(userInfo?.role_permission?.[0]?.role);
  const isLabBench = workspace === "lab";
  const isPhlebotomy = workspace === "phlebotomy";
  // No phlebotomist at this facility: the Lab User runs OPD orders end to end
  // (see LAB_ONLY_STATUSES). Absent/true keeps the two-role flow unchanged.
  // Who runs it: the Lab User, or a facility user who was granted lab management
  // ("manage lab service"), which lands in the unclassified "all" workspace.
  const hasLabServiceAccess =
    workspace === "all" &&
    permissions.some(
      (p) => p.permission_name === "manage lab service" && p.is_accessible,
    );
  const labOnlyMode =
    (isLabBench || hasLabServiceAccess) && userInfo?.has_phlebotomist === false;
  // Acknowledge is the phlebotomist's step: not on the lab bench, and not on the
  // OPD tab of a no-phlebotomist facility (every row there is OPD).
  const showAckColumn =
    !isLabBench && !(labOnlyMode && patientTypeFilter === "OPD");
  // The lab step at which this facility posts charges to IPD billing.
  const chargeCaptureStep = resolveChargeCaptureStep(userInfo);
  // One capture per order per session: the status dropdown can move a sample
  // back and forth across the billing step, and the ledger must not take a
  // second copy of the same test.
  const chargedOrderNosRef = useRef(new Set());

  // Which samples have gone out to a vendor, keyed by order number. Seeded from
  // the store so a reload doesn't lose the routing, and re-read after every
  // dispatch so the board relabels immediately.
  const [dispatchMap, setDispatchMap] = useState(readDispatchMap);
  const [dispatchModal, setDispatchModal] = useState(null);

  const [patients, setPatients] = useState([]);
  const patientsRef = useRef(patients);
  useEffect(() => {
    patientsRef.current = patients;
  }, [patients]);
  const statusPersistedRef = useRef(new Map());
  const sessionVisibleOrderNosRef = useRef(new Set());
  const sessionVisibleRowKeysRef = useRef(new Set());
  const [sessionTrackVersion, setSessionTrackVersion] = useState(0);
  const commitPatients = useCallback((updater) => {
    setPatients((prev) => {
      const next = updater(prev);
      patientsRef.current = next;
      return next;
    });
  }, []);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [kpiStats, setKpiStats] = useState(EMPTY_KPI_STATS);
  const [kpiStatsLoading, setKpiStatsLoading] = useState(true);
  // Local-only acknowledge timestamps, keyed by row id (see handleAcknowledgeBatch).
  const [acknowledgedByRow, setAcknowledgedByRow] = useState({});
  const [acknowledgingIds, setAcknowledgingIds] = useState(() => new Set());
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const expandedIdsRef = useRef(expandedIds);
  useEffect(() => {
    expandedIdsRef.current = expandedIds;
  }, [expandedIds]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  // No-phlebotomist OPD tab: only the four statuses the Lab User works with.
  // Other tabs keep the full list (plus Delivered/Uploaded, which OPD rows can reach).
  const statusFilterOptions =
    labOnlyMode && patientTypeFilter === "OPD"
      ? LAB_ONLY_STATUSES
      : labOnlyMode
        ? [...STATUS_FILTER_OPTIONS, ...LAB_ONLY_EXTRA_FILTERS]
        : STATUS_FILTER_OPTIONS;
  useEffect(() => {
    // A filter picked on another tab may not exist on this one.
    if (
      statusFilter !== "All statuses" &&
      !statusFilterOptions.includes(statusFilter)
    ) {
      setStatusFilter("All statuses");
    }
  }, [statusFilter, statusFilterOptions]);
  const [priorityFilter, setPriorityFilter] = useState("All priorities");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  const openDatePicker = (ref) => {
    if (ref.current?.showPicker) ref.current.showPicker();
    else ref.current?.focus();
  };
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannedDetails, setScannedDetails] = useState(null);
  const [highlightOrderNos, setHighlightOrderNos] = useState(() => new Set());
  const [observations, setObservations] = useState(() =>
    readLabObservationPanels(),
  );
  const [reportModal, setReportModal] = useState(null);
  const [rejectionModal, setRejectionModal] = useState(null);
  const [uploadModal, setUploadModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);

  const trackPatientInSession = useCallback((patient) => {
    if (!patient) return;
    let added = false;
    const rowKey = stableRowKey(patient);
    const adm = admissionIdForRow(patient);
    if (adm && !sessionVisibleRowKeysRef.current.has(adm)) {
      sessionVisibleRowKeysRef.current.add(adm);
      added = true;
    }
    if (rowKey && !sessionVisibleRowKeysRef.current.has(rowKey)) {
      sessionVisibleRowKeysRef.current.add(rowKey);
      added = true;
    }
    (patient.orders || []).forEach((o) => {
      const no = String(o?.orderNo || "");
      if (no && !sessionVisibleOrderNosRef.current.has(no)) {
        sessionVisibleOrderNosRef.current.add(no);
        added = true;
      }
    });
    if (added) setSessionTrackVersion((v) => v + 1);
  }, []);

  const handleObservationChange = (orderNo, panels) => {
    setObservations((prev) => ({ ...prev, [orderNo]: panels }));
    rememberLabReport(orderNo, {
      rows: flattenObservationPanels(panels),
      panels,
    });
  };

  const loadPatientOrdersRef = useRef(null);
  const fetchLabOrdersRef = useRef(null);
  const listFetchSeqRef = useRef(0);
  const detailFetchInFlightRef = useRef(new Map());

  // Debounce search → server query; reset to page 1 when the query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    patientTypeFilter,
    debouncedSearch,
    statusFilter,
    priorityFilter,
    dateFrom,
    dateTo,
  ]);

  // Encounter tab changes must not keep the previous tab's rows pinned via
  // sessionVisible* — those refs are filled by every rendered board row.
  useEffect(() => {
    sessionVisibleOrderNosRef.current = new Set();
    sessionVisibleRowKeysRef.current = new Set();
    setSessionTrackVersion((v) => v + 1);
  }, [patientTypeFilter]);

  // Tab/search/date/priority go to the server; status and pagination are
  // applied here (see fetchLabOrders).
  const kpiQuery = useMemo(
    () => ({
      patientType: patientTypeFilter,
      search: debouncedSearch,
      priority: priorityFilter,
      dateFrom,
      dateTo,
    }),
    [
      patientTypeFilter,
      debouncedSearch,
      priorityFilter,
      dateFrom,
      dateTo,
    ],
  );

  // [WORKSPACE_PAGING] The server pages get_patient_lab_tests/ before either
  // role's status filter is applied, so page 1 is mostly rows this workspace
  // then throws away: a lab user with 41 orders of their own was seeing 20 of
  // them, and a sample sent to them against an older order sat pages deep where
  // they could never reach it. The KPI strip already had to walk every page to
  // count honestly, so the list is cut from that same snapshot — filtered to
  // the workspace, ordered by last activity, then paged in the browser. One
  // request now serves both, so this is a page fewer than before, not more.
  const fetchLabOrders = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++listFetchSeqRef.current;
    if (!silent) setOrdersLoading(true);
    setKpiStatsLoading(true);
    try {
      const next = await fetchAllLabTestPatients(
        axios,
        getAuthHeaders(true),
        kpiQuery,
        { skipGlobalLoader: !!silent },
      );
      if (requestId !== listFetchSeqRef.current) return;
      commitPatients((prev) =>
        replacePatientsPreservingStatus(
          prev,
          next,
          statusPersistedRef.current,
          sessionVisibleOrderNosRef.current,
          {
            retainSessionExtras: !hasActiveLabListFilters({
              ...kpiQuery,
              status: statusFilter,
            }),
          },
        ),
      );
      const dispatchSnapshot = readDispatchMap();
      setKpiStats(
        computeLabDashboardStats(
          next
            .flatMap((p) =>
              labOnlyMode && isOpdPatientRow(p)
                ? p.orders
                : p.orders.filter((o) => statusInWorkspace(o.status, workspace)),
            )
            .filter(
              (o) =>
                !(
                  workspace === "lab" &&
                  isOutsourcedOrder(o.orderNo, dispatchSnapshot)
                ),
            ),
        ),
      );
      setOrdersError(null);
      if (isLabBench) {
        void syncLabDiagnosticRejections(
          patientsRef.current,
          commitPatients,
        );
      }
    } catch (err) {
      if (requestId !== listFetchSeqRef.current) return;
      const status = err.response?.status;
      const serverMessage =
        err.response?.data?.message || err.response?.data?.error;
      console.error(
        "Failed to fetch lab orders:",
        status,
        err.response?.data || err.message,
      );
      const detail = status
        ? `Could not load lab orders (${status}${serverMessage ? `: ${serverMessage}` : ""})`
        : "Could not load lab orders (network error)";
      setOrdersError(detail);
      if (!silent) toast.error(detail);
    } finally {
      if (requestId === listFetchSeqRef.current) {
        setOrdersLoading(false);
        setKpiStatsLoading(false);
      }
    }
  }, [kpiQuery, statusFilter, workspace, commitPatients, isLabBench, labOnlyMode]);

  fetchLabOrdersRef.current = fetchLabOrders;

  useEffect(() => {
    fetchLabOrdersRef.current?.({ silent: false });
  }, [fetchLabOrders]);

  const [detailLoadingIds, setDetailLoadingIds] = useState(() => new Set());

  // Detail refresh on expand via get_patient_lab_tests/?admission_id= or ?lab_test_id=
  // Returns merged orders so Acknowledge can charge-capture without a stale list snapshot.
  const loadPatientOrders = useCallback(
    async (patient, { silent = false } = {}) => {
      const isIpd =
        patient.patientType === "IPD" ||
        (patient.admissionId != null && patient.admissionId !== "");
      const detailKey = isIpd
        ? `adm:${patient.admissionId}`
        : `lt:${patient.labTestId || patient.id}`;
      if (!isIpd && !patient.labTestId && !patient.appointmentId) {
        return null;
      }
      if (isIpd && (patient.admissionId == null || patient.admissionId === "")) {
        return null;
      }

      const inflight = detailFetchInFlightRef.current.get(detailKey);
      if (inflight) return inflight;

      const run = (async () => {
        if (!silent) {
          setDetailLoadingIds((prev) => new Set(prev).add(detailKey));
        }
        try {
          const query = isIpd
            ? {
              admissionId: patient.admissionId,
              page: 1,
              pageSize: 50,
              patientType: "IPD",
            }
            : patient.labTestId
              ? { labTestId: patient.labTestId, page: 1, pageSize: 50 }
              : {
                appointmentId: patient.appointmentId,
                page: 1,
                pageSize: 50,
                patientType: "OPD",
              };

          const { patients: fetched } = await fetchLabTestsPage(
            axios,
            getAuthHeaders(true),
            query,
            { skipGlobalLoader: true },
          );

          const match =
            fetched.find((p) => p.id === patient.id) ||
            fetched.find(
              (p) =>
                isIpd &&
                String(p.admissionId) === String(patient.admissionId) &&
                String(p.labTestId || "") === String(patient.labTestId || ""),
            ) ||
            fetched.find(
              (p) =>
                isIpd &&
                String(p.admissionId) === String(patient.admissionId),
            ) ||
            fetched[0];

          if (!match) return null;

          const demographics = pickPatientDemographics({
            age: match.age,
            gender: match.gender,
            dob: match.dob,
            uhid: match.uhid,
            bed_no: match.bedNo,
            room_no: match.roomNo,
            abha_id: match.abhaId,
            doctor_name: match.doctorName,
            patient_phone_no: match.phone,
          });

          const existing =
            patientsRef.current.find((p) => samePatientRow(p, patient)) ||
            (isIpd
              ? patientsRef.current.find(
                  (p) =>
                    String(p.admissionId) === String(patient.admissionId),
                )
              : null);
          const orders = existing
            ? mergeOrderLists(
                existing.orders,
                match.orders,
                statusPersistedRef.current,
              )
            : match.orders;
          const patch = demographicsPatch(existing || {}, demographics);

          commitPatients((prev) => {
            const target =
              prev.find((p) => samePatientRow(p, patient)) ||
              (isIpd
                ? prev.find(
                    (p) =>
                      String(p.admissionId) === String(patient.admissionId),
                  )
                : null);
            if (!target) return prev;
            if (
              Object.keys(patch).length === 0 &&
              JSON.stringify(target.orders) === JSON.stringify(orders)
            ) {
              return prev;
            }
            return prev.map((p) =>
              p.id === target.id ? { ...p, ...patch, orders } : p,
            );
          });

          return orders;
        } catch (err) {
          console.error(
            `Failed to fetch orders for ${patient.name}:`,
            err.response?.data || err.message,
          );
          if (!silent) toast.error(`Could not load tests for ${patient.name}`);
          return null;
        } finally {
          if (!silent) {
            setDetailLoadingIds((prev) => {
              const next = new Set(prev);
              next.delete(detailKey);
              return next;
            });
          }
        }
      })();

      detailFetchInFlightRef.current.set(detailKey, run);
      try {
        return await run;
      } finally {
        detailFetchInFlightRef.current.delete(detailKey);
      }
    },
    [commitPatients],
  );

  loadPatientOrdersRef.current = loadPatientOrders;

  // Explode each admission into one top-level row per order-placement batch
  // when remembered/API batch keys differ. Sample types (Blood / Urine) stay
  // as separate groups inside the expanded table via groupOrdersByBatch.
  const patientRows = useMemo(() => {
    const boardCtx = {
      workspace,
      isLabBench,
      dispatchMap,
      sessionOrderNos: sessionVisibleOrderNosRef.current,
    };
    const rows = patients.flatMap((p) => {
      const labOnlyOpd = labOnlyMode && isOpdPatientRow(p);
      let visibleOrders = (p.orders || []).filter(
        (o) => labOnlyOpd || orderVisibleOnBoard(o, boardCtx),
      );
      if (visibleOrders.length === 0) {
        const adm = p.admissionId != null ? String(p.admissionId) : "";
        const pid = String(p.id || "");
        const inSession =
          sessionVisibleRowKeysRef.current.has(adm) ||
          sessionVisibleRowKeysRef.current.has(pid);
        if (inSession) {
          visibleOrders = (p.orders || []).filter((o) =>
            sessionVisibleOrderNosRef.current.has(String(o.orderNo)),
          );
        }
      }
      if (visibleOrders.length === 0) return [];
      const scoped = { ...p, orders: visibleOrders };
      const batches = groupOrdersByBatchOnly(visibleOrders);
      if (batches.length <= 1) return [scoped];
      return batches.map((b) => ({
        ...scoped,
        id: `${p.id}::${b.batchKey}`,
        orders: b.orders,
      }));
    });

    rows.forEach((row) => {
      const key = stableRowKey(row);
      (row.orders || []).forEach((o) => {
        const no = String(o?.orderNo || "");
        if (no) sessionVisibleOrderNosRef.current.add(no);
      });
      const adm = admissionIdForRow(row);
      if (adm) sessionVisibleRowKeysRef.current.add(adm);
      sessionVisibleRowKeysRef.current.add(key);
    });

    // Queue order by last activity — new Emergency/OPD/IPD orders and
    // phlebotomy "Send to in-house" (Sample Sent) land at the top instead of
    // sticking to the session's first-seen position at the bottom.
    return [...rows].sort(
      (a, b) =>
        latestActivityMs(b.orders) - latestActivityMs(a.orders) ||
        String(b.id || "").localeCompare(String(a.id || "")),
    );
  }, [patients, workspace, isLabBench, labOnlyMode, dispatchMap, sessionTrackVersion]);

  // Keep expanded rows open when a status write or silent refetch restamps ids.
  useEffect(() => {
    setExpandedIds((prev) => {
      const next = remapExpandedRowIds(prev, patientRows);
      if (next === prev) return prev;
      expandedIdsRef.current = next;
      return next;
    });
  }, [patientRows]);

  const allOrderGroups = useMemo(
    () =>
      patientRows.flatMap((p) =>
        groupOrdersByBatch(p.orders).map((g) => ({ ...g, patient: p })),
      ),
    [patientRows],
  );

  // IPD rows merge every order for the admission, so a row can hold tests at
  // several statuses — the status filter applies to the orders, not the row.
  const filteredPatients = useMemo(() => {
    let rows = patientRows;
    const typeFilter = String(patientTypeFilter || "").trim().toUpperCase();
    if (typeFilter && typeFilter !== "ALL") {
      rows = rows.filter(
        (p) => String(p.patientType || "").toUpperCase() === typeFilter,
      );
    }
    if (debouncedSearch) {
      rows = rows.filter((p) => patientMatchesLabSearch(p, debouncedSearch));
    }
    const statusActive = statusFilter !== "All statuses";
    const priorityActive = priorityFilter !== "All priorities";
    if (!statusActive && !priorityActive) return rows;
    return rows
      .map((p) => ({
        ...p,
        orders: (p.orders || []).filter((o) => {
          if (statusActive && !matchesStatusFilter(o, statusFilter)) {
            return false;
          }
          if (
            priorityActive &&
            normalizePriorityLabel(o.priority) !== priorityFilter
          ) {
            return false;
          }
          return true;
        }),
      }))
      .filter((p) => p.orders.length > 0);
  }, [
    patientRows,
    patientTypeFilter,
    statusFilter,
    priorityFilter,
    debouncedSearch,
    sessionTrackVersion,
  ]);

  // Paged here rather than by the server — see [WORKSPACE_PAGING].
  const totalCount = filteredPatients.length;
  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const pagedPatients = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPatients.slice(start, start + pageSize);
  }, [filteredPatients, page, pageSize]);

  const totalTests = pagedPatients.reduce((sum, p) => sum + p.orders.length, 0);

  // A silent refetch after a status post can shorten the list — moving a sample
  // on can empty the page the user is standing on, and paging is ours now, so
  // nothing else would pull them back into range.
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const toggleExpand = useCallback((row) => {
    const id = row.id;
    const opening = !isRowExpanded(row, expandedIdsRef.current);
    setExpandedIds((prev) => {
      // Only one patient accordion may stay open.
      const next = opening ? new Set([id]) : new Set(prev);
      if (!opening) {
        next.delete(id);
        const adm = admissionIdForRow(row);
        if (adm) {
          next.delete(adm);
          [...next].forEach((key) => {
            if (String(key).startsWith(`${adm}::`)) next.delete(key);
          });
        }
      }
      expandedIdsRef.current = next;
      return next;
    });
    if (opening) {
      trackPatientInSession(row);
      // The bulk list already includes this row's orders — expanding is
      // display-only. Detail refetch is reserved for Acknowledge / barcode scan
      // when the row truly has nothing cached yet.
      if (!(row.orders || []).length) {
        loadPatientOrdersRef.current?.(row);
      }
    }
  }, [trackPatientInSession]);

  const handleScan = () => {
    const value = barcodeInput.trim();
    if (!value) return;

    // A phone/2D scanner reads the full QR payload
    // (code|date|name|uhid|sample|tests); a user may instead type just the
    // short human-readable code (BC-LAB-…). Handle both.
    const decoded = parseBarcodePayload(value);
    const lookupCode = (decoded?.barcode || value).toLowerCase();
    const match = allOrderGroups.find(
      (g) => g.barcode.toLowerCase() === lookupCode,
    );

    if (!decoded && !match) {
      toast.error(`No order found for barcode "${value}"`);
      return;
    }

    // Reveal the details hidden in the barcode. Prefer the decoded payload;
    // when only a code was typed, pull the same fields from the live order.
    const details = decoded || {
      barcode: match.barcode,
      date: match.orders[0]?.ordered
        ? match.orders[0].ordered.slice(0, 10)
        : "-",
      name: match.patient.name,
      uhid: match.patient.uhid,
      sample: match.sample || "-",
      tests: match.orders.map((o) => o.service).join(", "),
    };
    setScannedDetails(details);

    if (match) {
      setExpandedIds(() => {
        const next = new Set([match.patient.id]);
        expandedIdsRef.current = next;
        return next;
      });
      if (!(match.patient.orders || []).length) {
        loadPatientOrdersRef.current?.(match.patient);
      }
      setHighlightOrderNos(new Set(match.orders.map((o) => o.orderNo)));
      window.setTimeout(() => setHighlightOrderNos(new Set()), 4000);
    }

    setBarcodeInput("");
  };

  // IPD → update_lab_test_status/; OPD → change_patient_lab_test_status/.
  // Applies the new status locally first and rolls back if the server rejects it.
  const applyOrderStatuses = useCallback(
    (patient, orderNos, status) => {
      const wireStatus = normalizeLabOrderStatus(status);
      const wanted = new Set(orderNos);
      const touchedAt = new Date().toISOString();
      orderNos.forEach((no) => {
        if (no) statusPersistedRef.current.set(String(no), wireStatus);
      });
      trackPatientInSession(patient);
      commitPatients((prev) =>
        prev.map((p) =>
          samePatientRow(p, patient)
            ? {
                ...p,
                orders: p.orders.map((o) =>
                  wanted.has(o.orderNo)
                    ? { ...o, status: wireStatus, sampleUpdatedAt: touchedAt }
                    : o,
                ),
              }
            : p,
        ),
      );
    },
    [commitPatients, trackPatientInSession],
  );

  const isIpdPatient = isIpdPatientRow;

  const resolveOpdLabTestId = (patient, order) =>
    String(order?.labTestId || patient?.labTestId || order?.orderNo || "").trim();

  const submitLabTestStatusUpdates = useCallback(async (patient, orders, status) => {
    if (isIpdPatient(patient)) {
      const results = await Promise.all(
        orders.map((o) =>
          axios.post(
            `${process.env.REACT_APP_BACKEND_ROUTE_URL}update_lab_test_status/`,
            {
              admission_id: String(patient.admissionId),
              order_no: o.orderNo,
              status,
            },
            { headers: getAuthHeaders(true), skipGlobalLoader: true },
          ),
        ),
      );
      results.forEach(assertStatusUpdateOk);
      return;
    }
    const labTestPayload = {
      lab_tests: orders.map((o) => ({
        lab_test_id: resolveOpdLabTestId(patient, o),
        status,
      })),
    };
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_ROUTE_URL}change_patient_lab_test_status/`,
      JSON.stringify(labTestPayload),
      { headers: getAuthHeaders(true), skipGlobalLoader: true },
    );
    assertStatusUpdateOk(res);
    // change_patient_lab_test_status/ answers 200 with one result per test; at a
    // no-phlebotomist facility a Failed one (e.g. not the Lab User) must roll back.
    if (labOnlyMode && Array.isArray(res?.data)) {
      const failed = res.data.find((r) => r?.status === "Failed");
      if (failed) throw new Error(failed.message || "Status update rejected");
    }
  }, [labOnlyMode]);

  // Bulk-posts the row's tests to the admission's billing ledger. Called from
  // whichever step `lab_chargecapture_step` names; IPD-only, since OPD lab
  // work is billed at the counter and never touches charge capture.
  // Throws if the post itself fails — the caller decides what that means for
  // the status it just wrote.
  const captureChargesForOrders = useCallback(async (patient, orders) => {
    const fresh = (orders || []).filter(
      (o) => !chargedOrderNosRef.current.has(String(o.orderNo)),
    );
    if (fresh.length === 0) return { captured: 0, reason: "already-captured" };

    const chargeDate = new Date().toISOString().split("T")[0];
    const charges = fresh
      .map((o) => ({
        medical_service_id: billingMedicalServiceId(o),
        quantity: 1,
        charge_date: chargeDate,
      }))
      .filter((c) => c.medical_service_id != null);

    if (charges.length === 0) return { captured: 0, reason: "no-codes" };
    if (charges.length < fresh.length) {
      toast.warn(
        `${fresh.length - charges.length} of ${fresh.length} test(s) for ${patient.name} have no billing code and won't be charge-captured`,
      );
    }

    await axios.post(
      `${process.env.REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${patient.admissionId}/billing/charge-capture/bulk-post/`,
      { charges },
      { headers: getAuthHeaders(true) },
    );
    fresh.forEach((o) => chargedOrderNosRef.current.add(String(o.orderNo)));
    return { captured: charges.length };
  }, []);

  const postOrderStatuses = useCallback(
    async (patient, orders, status) => {
      if (!patient) return false;
      const orderNos = orders.map((o) => o.orderNo);
      const previous = new Map(orders.map((o) => [o.orderNo, o.status]));
      applyOrderStatuses(patient, orderNos, status);
      try {
        await submitLabTestStatusUpdates(patient, orders, status);
      } catch (err) {
        console.error(
          `Failed to set status "${status}":`,
          err.response?.data || err.message,
        );
        previous.forEach((prevStatus, no) => {
          statusPersistedRef.current.set(String(no), prevStatus);
        });
        commitPatients((prev) =>
          prev.map((p) =>
            samePatientRow(p, patient)
              ? {
                  ...p,
                  orders: p.orders.map((o) =>
                    previous.has(o.orderNo)
                      ? { ...o, status: previous.get(o.orderNo) }
                      : o,
                  ),
                }
              : p,
          ),
        );
        toast.error(`Could not update status to "${status}". Please try again.`);
        return false;
      }

      // Billing follows the configured step. "Sample Acknowledged" never
      // arrives here — Acknowledge posts its own status and its own charges.
      if (
        status === chargeCaptureStep &&
        status !== "Sample Acknowledged" &&
        isIpdPatient(patient)
      ) {
        try {
          const { captured, reason } = await captureChargesForOrders(
            patient,
            orders,
          );
          if (captured > 0) {
            toast.success(
              `Captured charges for ${captured} test${captured === 1 ? "" : "s"} — ${patient.name}`,
            );
          } else if (reason === "no-codes") {
            toast.error(
              `Status updated for ${patient.name}, but could not capture charges — no billing code found for these tests`,
            );
          }
        } catch (err) {
          console.error(
            `Charge capture at "${status}" failed:`,
            err.response?.data || err.message,
          );
          toast.error(
            `Status updated for ${patient.name}, but charge capture failed — see console`,
          );
        }
      }
      return true;
    },
    [
      applyOrderStatuses,
      submitLabTestStatusUpdates,
      commitPatients,
      chargeCaptureStep,
      captureChargesForOrders,
    ],
  );

  // [LIS] The phlebotomist's in-house send is when a sample reaches the lab, so
  // that is when its tests are registered with the LIS — IPD, OPD and Emergency
  // alike. Vendor-dispatched samples never go to the in-house LIS. Not awaited:
  // the board must not wait on the external system.
  const sendSampleToLis = useCallback(
    (patient, orders) => {
      if (!userInfo?.lis_integrated || !patient) return;
      const doctor = orders.find((o) => o.doctor && o.doctor !== "-")?.doctor;
      sendLabSampleToLis({
        patientId: patient.patientId,
        patientType: String(patient.patientType || "").toUpperCase(),
        serviceIds: [
          ...new Set(
            orders
              .map((o) => o.medicalServiceNo ?? o.medicalServiceId)
              .filter((id) => id != null && id !== "" && id !== "-")
              .map(String),
          ),
        ],
        orderNos: Object.fromEntries(
          orders.map((o) => [String(o.medicalServiceNo ?? o.medicalServiceId), o.orderNo]),
        ),
        careContextId:
          patient.careContextId || orders[0]?.careContextReferenceId || "",
        appointmentId: patient.appointmentId || "",
        doctorName: doctor || "",
        headers: getAuthHeaders(),
      });
    },
    [userInfo],
  );

  // Opening the label is the moment the barcode physically exists, so the batch
  // moves Sample Acknowledged → Barcode Generated right then. Already-progressed
  // orders (collected/sent/…) are left alone — reprinting a label must not drag
  // a sample backwards.
  const handlePrintBarcode = useCallback(
    (group, patient) => {
      printBarcodeLabel(group, patient);
      const toStamp = group.orders.filter(
        (o) => o.status === "Sample Acknowledged",
      );
      if (!patient || String(patient.id).startsWith("demo-")) return;
      if (toStamp.length === 0) return;
      postOrderStatuses(patient, toStamp, "Barcode Generated");
    },
    [postOrderStatuses],
  );

  // `orders` is the whole sample row (see LabStatusSelect) — the tests in it
  // share a tube and a barcode, so they're moved as one. Tests already at the
  // target status are skipped so a mixed row doesn't re-post what's done.
  const handleOrderStatusChange = useCallback(
    async (orders, nextStatus, patient, group) => {
      const wireNext = isHandoverToLabStatus(nextStatus)
        ? "Sample Sent"
        : nextStatus;
      const toMove = (orders || []).filter((o) => o.status !== wireNext);
      if (!wireNext || toMove.length === 0) return;
      if (!patient) return;
      const keepExpanded = isRowExpanded(patient, expandedIdsRef.current);
      const alreadyDispatched = (orders || []).some(
        (o) => dispatchMap[String(o.orderNo)],
      );
      const preserveExpanded = () => {
        if (!keepExpanded) return;
        setExpandedIds(() => {
          const next = new Set([patient.id]);
          expandedIdsRef.current = next;
          return next;
        });
      };
      // IPD / Emergency phlebotomist picks in-house vs vendor before Sample Sent.
      // Vendor list loads inside the modal; no vendors still allows in-house.
      // Lab bench users never dispatch.
      if (
        wireNext === "Sample Sent" &&
        !alreadyDispatched &&
        resolveBillingScope(patient) &&
        !isLabBench
      ) {
        setDispatchModal({
          group: group || { orders },
          patient,
          toMove,
        });
        return;
      }
      preserveExpanded();
      const posted = postOrderStatuses(patient, toMove, wireNext);
      // OPD has no send-sample modal, so its in-house send lands here.
      if (wireNext === "Sample Sent" && !alreadyDispatched && !isLabBench) {
        posted.then((ok) => ok && sendSampleToLis(patient, toMove));
      }
    },
    [postOrderStatuses, dispatchMap, isLabBench, sendSampleToLis],
  );

  // Routing is recorded before the status post so the board relabels the moment
  // the row moves; if the post is rejected, both are rolled back together and
  // the sample reads as never having left.
  const handleConfirmDispatch = useCallback(
    async ({ vendor, dispatchRef }) => {
      const ctx = dispatchModal;
      if (!ctx) return;
      setDispatchModal(null);

      // In-house — same path as before outsourcing existed: post Sample Sent
      // to the backend so the lab user (mednovaLab) receives it on their board.
      if (!vendor) {
        const ok = await postOrderStatuses(
          ctx.patient,
          ctx.toMove,
          "Sample Sent",
        );
        if (ok) {
          toast.success(`Sample sent to lab — ${ctx.patient.name}`);
          sendSampleToLis(ctx.patient, ctx.toMove);
        }
        return;
      }

      const orderNos = ctx.toMove.map((o) => o.orderNo);
      let chargeEventsByOrder = {};
      const billingScope = resolveBillingScope(ctx.patient);

      // OPD has no vendor charge-capture endpoint — skip billing, same as
      // OPD in-house charges being skipped elsewhere on this board.
      if (billingScope && billingScope.type !== "opd") {
        try {
          const { chargeEventsByOrder: captured, captured: count, bulk } =
            await captureVendorChargesForOrders(
              billingScope,
              ctx.toMove,
              vendor.vendor_id,
              { dispatchRef },
            );
          chargeEventsByOrder = captured;
          if (count === 0) {
            toast.error(
              "Could not capture vendor charges — no billable tests on this sample",
            );
            return;
          }
          if (bulk?.summary?.failed > 0) {
            toast.warn(
              `Vendor charges captured for ${count} test${count === 1 ? "" : "s"}, but ${bulk.summary.failed} failed`,
            );
          }
          ctx.toMove.forEach((o) => {
            if (chargeEventsByOrder[o.orderNo]) {
              chargedOrderNosRef.current.add(String(o.orderNo));
            }
          });
        } catch (err) {
          console.error(
            "Vendor charge capture failed:",
            err.response?.data || err.message,
          );
          toast.error(
            err.response?.data?.message ||
              err.message ||
              "Could not capture vendor charge. Sample was not sent.",
          );
          return;
        }
      }

      if (vendor) {
        saveDispatch({
          orderNos,
          vendor,
          dispatchRef,
          chargeEventsByOrder,
        });
        setDispatchMap(readDispatchMap());
      }

      const ok = await postOrderStatuses(ctx.patient, ctx.toMove, "Sample Sent");
      if (!ok) {
        if (vendor) {
          clearDispatch(orderNos);
          setDispatchMap(readDispatchMap());
        }
        const eventIds = Object.values(chargeEventsByOrder).filter(Boolean);
        if (billingScope && eventIds.length) {
          await reverseVendorCharges(
            billingScope,
            eventIds,
            "Sample send failed after vendor charge capture",
          );
          ctx.toMove.forEach((o) => {
            chargedOrderNosRef.current.delete(String(o.orderNo));
          });
        }
        return;
      }
      if (vendor) {
        toast.success(
          billingScope?.type === "opd"
            ? `Dispatched to ${vendor.vendor_name}`
            : `Dispatched to ${vendor.vendor_name} — vendor charge captured`,
        );
      }
    },
    [dispatchModal, postOrderStatuses, sendSampleToLis],
  );

  // One report per sample row, covering every test on it — so the modal is
  // scoped to this row's tests rather than the patient's whole order list.
  const handleGenerateReport = useCallback((group, patient) => {
    trackPatientInSession({ ...patient, orders: group.orders });
    setReportModal({
      patient: { ...patient, orders: group.orders },
      focusOrderNo: group.primaryOrderNo,
    });
  }, [trackPatientInSession]);

  const handleOpenRejected = useCallback((group, patient, order) => {
    const knownReason = String(order?.rejectionReason || "").trim();
    setRejectionModal({
      group,
      patient,
      order,
      reason: knownReason,
      loading: !knownReason,
    });
    if (knownReason) return;
    const pltId = resolveOrderPatientLabTestId(order);
    if (!pltId) {
      setRejectionModal((prev) =>
        prev ? { ...prev, loading: false } : prev,
      );
      return;
    }
    fetchDiagnosticReview(pltId, { roleType: "pathologist" })
      .then((review) => {
        const reason = review?.rejection?.reason || "";
        setRejectionModal((prev) => {
          if (!prev || prev.order?.orderNo !== order.orderNo) return prev;
          return { ...prev, reason, loading: false };
        });
        if (!review?.rejection?.rejected && !reason) return;
        commitPatients((prev) =>
          prev.map((p) =>
            samePatientRow(p, patient)
              ? {
                  ...p,
                  orders: p.orders.map((o) =>
                    o.orderNo === order.orderNo
                      ? {
                          ...o,
                          reportPublishStatus: "rejected",
                          rejectionReason: reason || o.rejectionReason || "",
                        }
                      : o,
                  ),
                }
              : p,
          ),
        );
      })
      .catch(() => {
        setRejectionModal((prev) => {
          if (!prev || prev.order?.orderNo !== order.orderNo) return prev;
          return { ...prev, loading: false };
        });
      });
  }, [commitPatients]);

  const handleUploadReport = useCallback((patient, group) => {
    setUploadModal({
      abhaId: patient?.abhaId || "",
      patientId: patient?.patientId || "",
      patient,
      orders: group?.orders || [],
    });
  }, []);

  const patchOrderPatientLabTestId = useCallback(
    (patient, orderNo, patientLabTestId) => {
      if (!patient || !orderNo || !patientLabTestId) return;
      commitPatients((prev) =>
        prev.map((p) =>
          samePatientRow(p, patient)
            ? {
                ...p,
                orders: p.orders.map((o) =>
                  o.orderNo === orderNo ? { ...o, patientLabTestId } : o,
                ),
              }
            : p,
        ),
      );
      setReportModal((prev) => {
        if (!prev?.patient || !samePatientRow(prev.patient, patient)) {
          return prev;
        }
        return {
          ...prev,
          patient: {
            ...prev.patient,
            orders: prev.patient.orders.map((o) =>
              o.orderNo === orderNo ? { ...o, patientLabTestId } : o,
            ),
          },
        };
      });
    },
    [commitPatients],
  );

  const openAssignModal = useCallback(
    async (order, patient, groupOrders = []) => {
      if (!order?.orderNo || !patient) return;

      let patientLabTestId = resolveOrderPatientLabTestId(order);
      if (!patientLabTestId) {
        const loadingToast = toast.info("Loading test details…", {
          autoClose: false,
        });
        try {
          patientLabTestId = await lookupPatientLabTestId(
            axios,
            getAuthHeaders(true),
            { patient, order },
          );
        } finally {
          toast.dismiss(loadingToast);
        }
      }

      if (patientLabTestId) {
        patchOrderPatientLabTestId(patient, order.orderNo, patientLabTestId);
      }

      // The other tests on this sample go to the same doctors — the report
      // covers all of them, not just the first one ordered.
      const siblingOrders = (groupOrders || []).filter(
        (o) => o?.orderNo && o.orderNo !== order.orderNo,
      );
      const extraPatientLabTestIds = [];
      for (const sibling of siblingOrders) {
        let siblingId = resolveOrderPatientLabTestId(sibling);
        if (!siblingId) {
          siblingId = await lookupPatientLabTestId(
            axios,
            getAuthHeaders(true),
            { patient, order: sibling },
          );
        }
        if (siblingId) {
          patchOrderPatientLabTestId(patient, sibling.orderNo, siblingId);
          extraPatientLabTestIds.push(siblingId);
        }
      }

      const classificationName = order.department || "";
      trackPatientInSession(patient);
      setAssignModal({
        patient,
        orderNo: order.orderNo,
        groupOrderNos: [order.orderNo, ...siblingOrders.map((o) => o.orderNo)],
        extraPatientLabTestIds,
        patientLabTestId: patientLabTestId || null,
        classificationName,
        serviceGroupId: LAB_SERVICE_GROUP_ID,
        orderLabel: `${order.orderNo} · ${order.service || "Lab test"}${classificationName ? ` · ${classificationName}` : ""}`,
        departmentLabel: classificationName,
        roleType: "pathologist",
      });
    },
    [patchOrderPatientLabTestId, trackPatientInSession],
  );

  // Finalizing the report publishes it — status becomes Reported and only
  // then does the Report column switch from Generate to View.
  const handleConfirmResults = useCallback(
    async (order) => {
      const patient = reportModal?.patient;
      if (!order?.orderNo || !patient) return false;
      const rejected = isDiagnosticReportRejected(order);
      if (REPORT_PUBLISHED_STATUSES.includes(order.status) && !rejected) {
        return true;
      }

      const panels = observations[order.orderNo] || [];
      const rows = flattenObservationPanels(panels);
      if (rows.length === 0) {
        toast.error("Enter at least one result before finalizing the report.");
        return false;
      }

      const ok = await postOrderStatuses(patient, [order], "Reported");
      if (ok) {
        rememberLabReport(order.orderNo, {
          rows,
          panels,
          service: order.service,
          finalized: true,
        });
        saveLabReportToServer(order.orderNo, {
          rows,
          panels,
          service: order.service,
        });
        trackPatientInSession(patient);
        if (rejected) {
          markReviewRevised(order.orderNo);
          const reviewPatch = {
            reportPublishStatus: "pending",
            rejectionReason: "",
          };
          commitPatients((prev) =>
            prev.map((p) =>
              samePatientRow(p, patient)
                ? {
                    ...p,
                    orders: p.orders.map((o) =>
                      o.orderNo === order.orderNo ? { ...o, ...reviewPatch } : o,
                    ),
                  }
                : p,
            ),
          );
          setReportModal((prev) => {
            if (!prev?.patient) return prev;
            return {
              ...prev,
              patient: {
                ...prev.patient,
                orders: prev.patient.orders.map((o) =>
                  o.orderNo === order.orderNo ? { ...o, ...reviewPatch } : o,
                ),
              },
            };
          });
        }
        toast.success(
          rejected
            ? `Report revised for ${order.orderNo} — submit for review again`
            : `Report finalized for ${order.orderNo}`,
        );
      }
      return ok;
    },
    [reportModal, postOrderStatuses, observations, commitPatients, trackPatientInSession],
  );

  const handleSubmitForReview = useCallback(
    (order, groupOrders) => {
      if (!canAssignDiagnosticDoctor) {
        toast.error("You do not have permission to assign diagnostic doctors");
        return;
      }
      openAssignModal(order, reportModal?.patient, groupOrders);
    },
    [canAssignDiagnosticDoctor, openAssignModal, reportModal?.patient],
  );

  // Builds the same PDF the report modal's Download button produces, but for
  // every published order on the patient at once. Results come from whatever
  // this session has typed, then the stored report, then the order's own rows.
  const handleDownloadConsolidated = useCallback(
    async (patient, { readyCount, testsCount, allReady }) => {
      const published = (patient.orders || []).filter((o) =>
        isReportGenerated(o),
      );
      if (published.length === 0) {
        toast.error(`No finalized report available for ${patient.name} yet`);
        return;
      }
      if (!allReady) {
        toast.info(
          `${readyCount}/${testsCount} reports ready for ${patient.name} — downloading available reports`,
        );
      }

      const resolveRows = (order) => {
        const live = flattenObservationPanels(observations[order.orderNo]);
        if (live.length > 0) return live;
        const stored = readLabReport(order.orderNo);
        if (stored?.rows?.length) return stored.rows;
        return normalizeLabResultRows(
          order.results || order.test_results || order.parameters,
        );
      };

      await downloadLabReport({
        patient: {
          ...patient,
          age: computeAge(patient.dob) ?? patient.age ?? null,
        },
        generatedAt: new Date().toISOString(),
        technician: withDoctorPrefix(
          sessionStorage.getItem("labTechnicianName") ||
            published[0]?.technician ||
            "",
        ),
        brandingData,
        userInfo,
        withLetterhead: true,
        orders: published.map((o) => ({
          orderNo: o.orderNo,
          service: o.service,
          cpt: o.cpt,
          ordered: o.ordered,
          doctor: o.doctor,
          department: o.department,
          technician: o.technician,
          status: o.status,
          pending: false,
          rows: resolveRows(o),
          interpretation: o.interpretation || null,
          patientLabTestId: resolveOrderPatientLabTestId(o),
        })),
      });
    },
    [observations, brandingData, userInfo],
  );

  // Acknowledge: IPD → update_lab_test_status; OPD → change_patient_lab_test_status.
  // Charge capture (bulk-post) is IPD-only — OPD skips billing entirely — and
  // only happens here when `lab_chargecapture_step` names this step; otherwise
  // the charges go out from postOrderStatuses at whichever step is configured.
  // Status is applied as soon as the status API succeeds; a missing billing
  // code must not leave the row stuck on Ordered after a successful write.
  const handleAcknowledgeBatch = useCallback(
    async (row) => {
      if (acknowledgingIds.has(row.id)) return;
      const isIpd = isIpdPatient(row);
      if (isIpd && (row.admissionId == null || row.admissionId === "")) {
        toast.error(`Could not acknowledge ${row.name} — missing admission id`);
        return;
      }
      if (!isIpd && !row.labTestId && row.orders.every((o) => !o.orderNo)) {
        toast.error(`Could not acknowledge ${row.name} — missing order id`);
        return;
      }

      setAcknowledgingIds((prev) => new Set(prev).add(row.id));
      let statusPosted = false;
      try {
        let ordersToAck = row.orders;
        if (isIpd && !expandedIdsRef.current.has(row.id)) {
          setExpandedIds(() => {
            const next = new Set([row.id]);
            expandedIdsRef.current = next;
            return next;
          });
          const detailOrders = await loadPatientOrders(row);
          if (Array.isArray(detailOrders) && detailOrders.length > 0) {
            const wanted = new Set(row.orders.map((o) => o.orderNo));
            const enriched = detailOrders.filter((o) => wanted.has(o.orderNo));
            ordersToAck = enriched.length > 0 ? enriched : detailOrders;
          }
        }

        await submitLabTestStatusUpdates(row, ordersToAck, "Sample Acknowledged");
        statusPosted = true;
        applyOrderStatuses(
          row,
          ordersToAck.map((o) => o.orderNo),
          "Sample Acknowledged",
        );
        setAcknowledgedByRow((prev) => ({
          ...prev,
          [row.id]: new Date().toISOString(),
        }));

        const ackedCount = `${ordersToAck.length} test${ordersToAck.length === 1 ? "" : "s"}`;

        if (isIpd && chargeCaptureStep === "Sample Acknowledged") {
          const { captured, reason } = await captureChargesForOrders(
            row,
            ordersToAck,
          );
          if (reason === "no-codes") {
            toast.error(
              `Acknowledged ${row.name}, but could not capture charges — no billing code found for these tests`,
            );
            return;
          }
          toast.success(
            captured > 0
              ? `Acknowledged and captured charges for ${ackedCount} — ${row.name}`
              : `Acknowledged ${ackedCount} — ${row.name}`,
          );
        } else {
          toast.success(`Acknowledged ${ackedCount} — ${row.name}`);
        }
      } catch (err) {
        console.error(
          `Failed to acknowledge for ${row.name}:`,
          err.response?.data || err.message,
        );
        if (statusPosted) {
          toast.error(
            `Acknowledged ${row.name}, but charge capture failed — see console`,
          );
        } else {
          toast.error(
            `Could not acknowledge ${row.name} — see console for details`,
          );
        }
      } finally {
        setAcknowledgingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [
      acknowledgingIds,
      submitLabTestStatusUpdates,
      loadPatientOrders,
      applyOrderStatuses,
      chargeCaptureStep,
      captureChargesForOrders,
    ],
  );

  const detailKeyFor = (p) =>
    p.patientType === "IPD" || p.admissionId != null
      ? `adm:${p.admissionId}`
      : `lt:${p.labTestId || p.id}`;

  return (
    <div>
      <KpiStrip stats={kpiStats} loading={kpiStatsLoading} />

      {/* No barcodes on the OPD tab when the facility has no phlebotomist. */}
      {!(labOnlyMode && patientTypeFilter === "OPD") && (
      <div
        style={{
          ...cardStyle,
          padding: 14,
          marginBottom: 16,
          display: "flex",
          gap: 10,
        }}
      >
        <input
          value={barcodeInput}
          onChange={(e) => setBarcodeInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleScan()}
          placeholder="Scan or type barcode (e.g. BC-LAB-2026-0005) and press Enter"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="button"
          onClick={handleScan}
          style={{
            padding: "9px 20px",
            borderRadius: 8,
            border: "none",
            background: C.primary,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Scan
        </button>
      </div>
      )}

      {scannedDetails && !(labOnlyMode && patientTypeFilter === "OPD") && (
        <div
          style={{
            ...cardStyle,
            padding: 16,
            marginBottom: 16,
            borderLeft: `4px solid ${C.primary}`,
            position: "relative",
          }}
        >
          <button
            type="button"
            onClick={() => setScannedDetails(null)}
            title="Dismiss"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: C.textSub,
              fontSize: 18,
              lineHeight: 1,
              fontFamily: "inherit",
            }}
          >
            ×
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <SvgIcon name="flask" size={16} color={C.primary} />
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Scanned barcode details
            </span>
            <span
              style={{
                fontSize: 12,
                fontFamily: "monospace",
                color: C.primary,
                fontWeight: 700,
              }}
            >
              {scannedDetails.barcode || "-"}
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
            }}
          >
            {[
              { label: "Date", value: scannedDetails.date },
              { label: "Patient", value: scannedDetails.name },
              { label: "UHID", value: scannedDetails.uhid },
              { label: "Sample", value: scannedDetails.sample },
              { label: "Test(s) to be performed", value: scannedDetails.tests },
            ].map((f) => (
              <div key={f.label}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.textSub,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 3,
                  }}
                >
                  {f.label}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: C.text,
                    wordBreak: "break-word",
                  }}
                >
                  {f.value || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient, UHID, order#, doctor, service…"
          style={{ ...inputStyle, flex: "1 1 260px" }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ ...inputStyle, width: 170 }}
        >
          <option>All statuses</option>
          {statusFilterOptions.map((s) => (
            <option key={s} value={s} style={{ textTransform: "lowercase" }}>
              {STATUS_FILTER_LABELS[s] || s}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={{ ...inputStyle, width: 150 }}
        >
          <option>All priorities</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <style>{`
          .ipd-date-input::-webkit-calendar-picker-indicator { opacity: 0; position: absolute; right: 0; width: 28px; height: 100%; cursor: pointer; }
        `}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ position: "relative" }}>
            <input
              ref={dateFromRef}
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="ipd-date-input"
              style={{ ...inputStyle, width: 150, paddingRight: 32 }}
              title="From date"
            />
            <span
              onClick={() => openDatePicker(dateFromRef)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <SvgIcon name="calendar" size={14} color={C.textSub} />
            </span>
          </div>
          <span style={{ fontSize: 12, color: C.textSub }}>to</span>
          <div style={{ position: "relative" }}>
            <input
              ref={dateToRef}
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="ipd-date-input"
              style={{ ...inputStyle, width: 150, paddingRight: 32 }}
              title="To date"
            />
            <span
              onClick={() => openDatePicker(dateToRef)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <SvgIcon name="calendar" size={14} color={C.textSub} />
            </span>
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            color: C.textSub,
            whiteSpace: "nowrap",
            marginLeft: "auto",
          }}
        >
          {pagedPatients.length} patient
          {pagedPatients.length === 1 ? "" : "s"} · {totalTests} test
          {totalTests === 1 ? "" : "s"} · {totalCount} total
        </span>
        <button
          type="button"
          title="Refresh orders"
          onClick={() => {
            const empty = new Set();
            expandedIdsRef.current = empty;
            setExpandedIds(empty);
            fetchLabOrdersRef.current?.({ silent: false });
          }}
          disabled={ordersLoading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 8,
            border: `1.5px solid ${C.border}`,
            background: "#fff",
            color: C.text,
            fontSize: 12,
            fontWeight: 600,
            cursor: ordersLoading ? "default" : "pointer",
            fontFamily: "inherit",
            opacity: ordersLoading ? 0.6 : 1,
          }}
        >
          <SvgIcon name="refresh" size={14} color={C.textSub} />
          {ordersLoading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div style={{ ...cardStyle, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                background: "#f8f9fa",
                fontSize: 11,
                fontWeight: 700,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              <th style={{ padding: "10px 12px" }}></th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>
                Patient
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>UHID</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Tests</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Ready</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>
                Pending
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>
                Latest order
              </th>
              {showAckColumn && (
                <th style={{ textAlign: "left", padding: "10px 12px" }}>
                  Acknowledge
                </th>
              )}
              <th style={{ textAlign: "left", padding: "10px 12px" }}>
                Consolidated report
              </th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading && patients.length === 0 ? (
              <tr>
                <td
                  colSpan={showAckColumn ? 9 : 8}
                  style={{
                    padding: 32,
                    textAlign: "center",
                    color: C.textSub,
                    fontSize: 13,
                  }}
                >
                  Loading lab orders…
                </td>
              </tr>
            ) : ordersError && patients.length === 0 ? (
              <tr>
                <td
                  colSpan={showAckColumn ? 9 : 8}
                  style={{
                    padding: 32,
                    textAlign: "center",
                    color: C.danger,
                    fontSize: 13,
                  }}
                >
                  {ordersError}
                </td>
              </tr>
            ) : pagedPatients.length === 0 ? (
              <tr>
                <td
                  colSpan={showAckColumn ? 9 : 8}
                  style={{
                    padding: 32,
                    textAlign: "center",
                    color: C.textSub,
                    fontSize: 13,
                  }}
                >
                  No matching orders.
                </td>
              </tr>
            ) : (
              pagedPatients.map((p) => (
                <PatientRow
                  key={p.id}
                  patient={p}
                  expanded={isRowExpanded(p, expandedIds)}
                  detailLoading={
                    detailLoadingIds.has(detailKeyFor(p)) &&
                    !(p.orders || []).length
                  }
                  onToggle={toggleExpand}
                  highlightOrderNos={highlightOrderNos}
                  onPrintBarcode={handlePrintBarcode}
                  onStatusChange={handleOrderStatusChange}
                  isLabBench={isLabBench}
                  isPhlebotomy={isPhlebotomy}
                  labOnlyMode={labOnlyMode}
                  dispatchMap={dispatchMap}
                  onGenerateReport={handleGenerateReport}
                  onOpenRejected={handleOpenRejected}
                  onUploadReport={handleUploadReport}
                  onDownloadConsolidated={handleDownloadConsolidated}
                  onAcknowledge={handleAcknowledgeBatch}
                  acknowledgedAt={acknowledgedByRow[p.id]}
                  acknowledging={acknowledgingIds.has(p.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 12,
          padding: "8px 4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.textSub }}>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE);
              setPage(1);
            }}
            style={{ ...inputStyle, width: 80 }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: C.textSub }}>
            Page {page}
            {` of ${totalPages}`}
            {` · showing ${pagedPatients.length} of ${totalCount}`}
          </span>
          <button
            type="button"
            disabled={page <= 1 || ordersLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: page <= 1 || ordersLoading ? "default" : "pointer",
              opacity: page <= 1 || ordersLoading ? 0.5 : 1,
              fontFamily: "inherit",
            }}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={
              ordersLoading ||
              page >= totalPages
            }
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: ordersLoading ? "default" : "pointer",
              opacity:
                ordersLoading ||
                  page >= totalPages
                  ? 0.5
                  : 1,
              fontFamily: "inherit",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {rejectionModal && (
        <RejectedReportDialog
          order={rejectionModal.order}
          reason={rejectionModal.reason}
          loading={rejectionModal.loading}
          onClose={() => setRejectionModal(null)}
          onRegenerate={() => {
            const { group, patient } = rejectionModal;
            setRejectionModal(null);
            handleGenerateReport(group, patient);
          }}
        />
      )}

      {uploadModal && (
        <UploadFileModal
          isOpen={!!uploadModal}
          handleClose={() => setUploadModal(null)}
          abhaId={uploadModal.abhaId}
          patientId={uploadModal.patientId}
          onUploadSuccess={async () => {
            const { patient, orders } = uploadModal;
            if (!patient || !orders?.length) return;
            const toReport = orders.filter(
              (o) => !REPORT_PUBLISHED_STATUSES.includes(o.status),
            );
            if (toReport.length === 0) return;
            await postOrderStatuses(patient, toReport, "Reported");
          }}
        />
      )}

      {dispatchModal && (
        <DispatchToVendorModal
          group={dispatchModal.group}
          patient={dispatchModal.patient}
          onClose={() => setDispatchModal(null)}
          onConfirm={handleConfirmDispatch}
        />
      )}

      {reportModal && (
        <LabReportModal
          patient={reportModal.patient}
          focusOrderNo={reportModal.focusOrderNo}
          observations={observations}
          onObservationChange={handleObservationChange}
          onConfirmResults={handleConfirmResults}
          onSubmitForReview={handleSubmitForReview}
          canSubmitForReview={canAssignDiagnosticDoctor && isLabBench}
          readOnly={isPhlebotomy}
          onClose={() => setReportModal(null)}
        />
      )}

      {assignModal && (
        <AssignDiagnosticDoctorsModal
          open={!!assignModal}
          onClose={() => setAssignModal(null)}
          patientLabTestId={assignModal.patientLabTestId}
          classificationName={assignModal.classificationName}
          serviceGroupId={assignModal.serviceGroupId}
          orderLabel={assignModal.orderLabel}
          departmentLabel={assignModal.departmentLabel}
          roleType={assignModal.roleType}
          extraPatientLabTestIds={assignModal.extraPatientLabTestIds}
          onAssigned={() => {
            const { patient, orderNo, groupOrderNos = [orderNo] } = assignModal;
            if (patient && orderNo) {
              groupOrderNos.forEach((no) => markReviewSubmitted(no));
              trackPatientInSession(patient);
              commitPatients((prev) =>
                prev.map((p) =>
                  samePatientRow(p, patient)
                    ? {
                        ...p,
                        orders: p.orders.map((o) =>
                          groupOrderNos.includes(o.orderNo)
                            ? { ...o, reportPublishStatus: "pending" }
                            : o,
                        ),
                      }
                    : p,
                ),
              );
            }
            setAssignModal(null);
          }}
        />
      )}
    </div>
  );
}
